import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Hoot from "../Hoot";
import RegisterOrUpdateContactModal from "./Modals/RegisterOrUpdateContactModal";
import { toast } from "react-toastify";
import ReactApexChart from "react-apexcharts";
import { Modal, Button, Tabs, Tab, Dropdown, Spinner, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  LuScanEye,
  LuStickyNote,
  LuDownload,
  LuListChecks,
  LuBookmarkPlus,
  LuBookmarkCheck,
  LuTrash2,
  LuFileDown,
  LuPlus,
  LuImage,
  LuChevronDown,
  LuChevronUp,
  LuSend,
  LuPencil,
  LuCheck,
  LuX,
  LuSearch,
  LuUpload,
  LuLoader,
} from "react-icons/lu";
import { FiEdit3, FiUsers } from "react-icons/fi";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import html2pdf from "html2pdf.js";

/* =========================
   HELPERS / UTILS
========================= */

const normalize = (str) =>
  str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

const safeValue = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

const provinciasList = [
  "Bengo","Benguela","Bié","Cabinda","Cuando Cubango","Cuanza Norte","Cuanza Sul",
  "Cunene","Huambo","Huíla","Luanda","Lunda Norte","Lunda Sul","Malanje",
  "Moxico","Namibe","Uíge","Zaire",
];

const statusOptions = [
  { label: "Parado", value: "parado" },
  { label: "Contactado", value: "contacto" },
  { label: "Em andamento / Apresentado", value: "em andamento" },
  { label: "Em uso", value: "em uso" },
  { label: "Rejeitado", value: "rejeitado" },
  { label: "Não atende", value: "não atende" },
  { label: "Interessado", value: "interessado" },
];

const statusLabel = (value) => {
  const v = normalize(value);
  if (v === "parado") return "Parado";
  if (v === "contacto" || v === "contactado") return "Contactado";
  if (v === "em andamento" || v === "emandamento") return "Em andamento / Apresentado";
  if (v === "em uso" || v === "emuso") return "Em uso";
  if (v === "rejeitado") return "Rejeitado";
  if (v === "nao atende" || v === "não atende") return "Não atende";
  if (v === "interessado") return "Interessado";
  return safeValue(value, "Sem estado");
};

const statusLabelPDF = (value) => {
  const v = normalize(value);
  if (v === "em andamento" || v === "emandamento") return "Em And. / Apresentado";
  return statusLabel(value);
};

const getStatusColorHex = (status) => {
  const s = normalize(status);
  if (s === "parado") return "#ef4444";
  if (s === "contacto" || s === "contactado") return "#22c55e";
  if (s === "em andamento" || s === "emandamento") return "#f59e0b";
  if (s === "em uso" || s === "emuso") return "#3b82f6";
  if (s === "rejeitado") return "#64748b";
  if (s === "nao atende" || s === "não atende") return "#f97316";
  if (s === "interessado") return "#14b8a6";
  return "#6366f1";
};

const getStatusClass = (status) => {
  const s = normalize(status);
  if (s === "parado") return "s-parado";
  if (s === "contacto" || s === "contactado") return "s-contacto";
  if (s === "em andamento" || s === "emandamento") return "s-em_andamento";
  if (s === "em uso" || s === "emuso") return "s-uso";
  if (s === "rejeitado") return "s-rejeitado";
  if (s === "nao atende" || s === "não atende") return "s-nao_atende";
  if (s === "interessado") return "s-interessado";
  return "s-outro";
};

const stringToColor = (string) => {
  if (!string) return "#9ca3af";
  let hash = 0;
  for (let i = 0; i < string.length; i++) hash = string.charCodeAt(i) + ((hash << 5) - hash);
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }
  return color;
};

const getTypeBadge = (type) => {
  let label = "Colégio";
  let color = "#6366f1";
  let t = type * 1;
  if (t === 1) { label = "Universidade"; color = "#f59e0b"; }
  return (
    <span className="pill" style={{ background: `${color}18`, color, borderColor: `${color}2a` }}>
      <i className="bi bi-mortarboard-fill" />
      {label}
    </span>
  );
};

const formatDatePT = (isoOrDate) => {
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    return d.toLocaleDateString("pt-PT");
  } catch { return "—"; }
};

const toISODateOnly = (d) => {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ""; }
};

const escapeHtml = (s) =>
  (s ?? "").toString()
    .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#039;");

/* =========================
   CLOUDINARY IMAGE UPLOAD HELPER
========================= */

const uploadImageFromUrl = async (imageUrl) => {
  const response = await fetch(imageUrl);
  if (!response.ok) throw new Error("Não foi possível carregar a imagem do URL.");
  const blob = await response.blob();
  const contentType = blob.type || "image/jpeg";
  const ext = contentType.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
  const fileName = `school_image_${Date.now()}.${ext}`;
  const file = new File([blob], fileName, { type: contentType });
  const formData = new FormData();
  formData.append("image", file);
  const res = await axios.post(Hoot() + "contacts/upload-image", formData, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });
  const uploadedUrl = res?.data?.url || res?.data?.data?.url || res?.data?.secure_url;
  if (!uploadedUrl) throw new Error("Resposta do servidor sem URL.");
  return uploadedUrl;
};

/* =========================
   STYLES
========================= */

const contactsStyles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

:root{
  --primary:#6051ff;
  --primary2:#7c3aed;
  --bg:#f4f6fb;
  --card:#ffffff;
  --border:#e6eaf2;
  --text:#0f172a;
  --muted:#64748b;
  --shadow: 0 14px 40px rgba(15,23,42,.08);
  --font: 'Plus Jakarta Sans', sans-serif;
}

* { font-family: var(--font) !important; }

.contacts-shell{ background:var(--bg); padding:10px; }
.contacts-card{
  background:var(--card);
  border-radius:18px;
  border:1px solid var(--border);
  padding:12px 14px 14px;
  box-shadow: var(--shadow);
  min-height:400px !important;
}

.contacts-header{
  display:flex;
  flex-wrap:wrap;
  justify-content:space-between;
  align-items:flex-start;
  gap:10px;
  margin-bottom:10px;
}

.pretty-modal .modal-body{
  height:83vh !important; 
  max-height:83vh !important; 
}

.contacts-header-title{
  font-weight:700;
  font-size:1.02rem;
  display:flex;
  align-items:center;
  gap:.55rem;
  color:var(--text);
}
.contacts-chip{
  display:inline-flex;
  align-items:center;
  gap:.35rem;
  padding:.28rem .62rem;
  border-radius:999px;
  background:linear-gradient(135deg, rgba(96,81,255,.14), rgba(124,58,237,.12));
  color:#4338ca;
  font-size:.78rem;
  font-weight:600;
  border:1px solid rgba(96,81,255,.16);
}

.contacts-toolbar{
  margin-top:6px;
  padding:10px 10px;
  border-radius:14px;
  background:linear-gradient(180deg, rgba(148,163,184,0.12), rgba(148,163,184,0.06));
  border:1px solid rgba(226,232,240,.7);
  margin-bottom:10px;
}

.kpi-row{
  display:grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap:10px;
  margin:10px 0 12px;
}
@media (max-width: 1200px){ .kpi-row{ grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 520px){ .kpi-row{ grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.kpi{
  background:var(--card);
  border:1px solid var(--border);
  border-radius:16px;
  padding:10px 12px;
  box-shadow: 0 10px 28px rgba(2,6,23,.06);
  position:relative;
  overflow:hidden;
}
.kpi::before{
  content:"";
  position:absolute;
  inset:-40px -60px auto auto;
  width:120px; height:120px;
  border-radius:999px;
  background: radial-gradient(circle at 30% 30%, rgba(96,81,255,.18), transparent 60%);
}
.kpi-title{ display:flex; align-items:center; gap:8px; font-size:.8rem; color:var(--muted); font-weight:600; }
.kpi-value{ margin-top:2px; font-size:1.18rem; font-weight:650; color:var(--text); }
.kpi-sub{ margin-top:2px; font-size:.74rem; color:var(--muted); }

/* tabela */
.contacts-table-wrapper{ width:100%; }
@media (max-width:768px){
  .contacts-table-desktop{display:none;}
  .contacts-mobile-list{display:block;}
}
@media (min-width:769px){
  .contacts-table-desktop{display:block;}
  .contacts-mobile-list{display:none;}
}

.contacts-table{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  font-size:.86rem;
  color:var(--text);
}
.contacts-table thead th{
  text-align:left;
  padding:.62rem .65rem;
  font-size:.78rem;
  font-weight:650;
  color:var(--muted);
  border-bottom:1px solid var(--border);
  background:var(--card);
  position:sticky;
  top:0;
  z-index:1;
}
.contacts-table tbody td{ padding:.62rem .65rem; border-bottom:1px solid var(--border); vertical-align:middle; }
.contacts-table tbody tr:hover{ background:rgba(148,163,184,0.10); }

/* INLINE NOTES ROW */
.notes-inline-row td {
  padding: 0 !important;
  border-bottom: 2px solid rgba(96,81,255,.18) !important;
  background: linear-gradient(180deg, #fafbff 0%, #f5f6ff 100%) !important;
}
.notes-inline-panel {
  padding: 0;
  animation: slideDown .22s cubic-bezier(.4,0,.2,1);
}
@keyframes slideDown {
  from { opacity:0; transform:translateY(-8px); }
  to { opacity:1; transform:translateY(0); }
}
.notes-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(96,81,255,.10);
}
.notes-inline-title {
  font-size: .82rem;
  font-weight: 750;
  color: var(--primary);
  display: flex;
  align-items: center;
  gap: 6px;
}
.notes-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px 16px;
}
.note-item {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(226,232,240,.6);
  align-items: flex-start;
}
.note-item:last-child { border-bottom: none; }
.note-avatar {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-size: .72rem;
  font-weight: 800;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,.12);
}
.note-body { flex: 1; min-width: 0; }
.note-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.note-author { font-size: .74rem; font-weight: 700; color: var(--text); }
.note-date { font-size: .72rem; color: var(--muted); }
.note-text { font-size: .82rem; color: #334155; line-height: 1.5; white-space: pre-wrap; }
.note-new-area {
  padding: 10px 16px 14px;
  border-top: 1px solid rgba(96,81,255,.10);
  background: rgba(96,81,255,.03);
}
.note-new-label {
  font-size: .74rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.note-textarea {
  width: 100%;
  border: 1.5px solid rgba(96,81,255,.25);
  border-radius: 12px;
  padding: 9px 12px;
  font-size: .82rem;
  color: var(--text);
  background: #fff;
  resize: vertical;
  min-height: 62px;
  outline: none;
  transition: border-color .15s;
  font-family: var(--font) !important;
  line-height: 1.55;
}
.note-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(96,81,255,.10); }
.note-send-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 7px;
  padding: 6px 16px;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  border: none;
  font-size: .78rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .12s, transform .1s;
}
.note-send-btn:hover { opacity: .88; }
.note-send-btn:active { transform: scale(.97); }
.note-send-btn:disabled { opacity: .5; cursor: not-allowed; }

.pill{
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:999px;
  font-size:.74rem; font-weight:600;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(226,232,240,.55);
  color:#0f172a;
}
.pill i{ opacity:.85; }

.contacts-avatar{
  width:40px; height:40px; border-radius:10px;
  display:inline-grid; place-items:center;
  font-size:.78rem; font-weight:700; color:#f9fafb;
  box-shadow: 0 10px 22px rgba(2,6,23,.10);
  justify-content:center;
  min-width:40px;
}

.status-badge{
  display:inline-flex; align-items:center; gap:8px;
  padding:6px 10px; border-radius:999px;
  font-size:.74rem; font-weight:650;
  border:1px solid rgba(0,0,0,.06);
  letter-spacing:.2px;
}
.status-dot{ width:8px; height:8px; border-radius:999px; background:currentColor; box-shadow: 0 0 0 4px rgba(0,0,0,.04); }

.s-parado{ background: rgba(239,68,68,.14); color:#ef4444; }
.s-contacto{ background: rgba(34,197,94,.14); color:#22c55e; }
.s-em_andamento{ background: rgba(245,158,11,.18); color:#f59e0b; }
.s-uso{ background: rgba(59,130,246,.14); color:#3b82f6; }
.s-rejeitado{ background: rgba(148,163,184,.20); color:#64748b; }
.s-nao_atende{ background: rgba(249,115,22,.16); color:#f97316; }
.s-interessado{ background: rgba(20,184,166,.14); color:#14b8a6; }
.s-outro{ background: rgba(99,102,241,.14); color:#6366f1; }

.btn-icon{
  width:34px; height:34px; border-radius:12px;
  border:1px solid rgba(226,232,240,.95);
  background:#fff; display:inline-grid; place-items:center;
  cursor:pointer; transition: transform .08s ease, background .12s ease;
}
.btn-icon:hover{ background: rgba(96,81,255,.06); }
.btn-icon:active{ transform: scale(.98); }

/* NOTES ICON WRAPPER — para badge de contagem */
.notes-btn-wrapper {
  position: relative;
  display: inline-flex;
}
.notes-count-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 17px;
  height: 17px;
  border-radius: 999px;
  background: #f97316;
  color: #fff;
  font-size: .62rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 1.5px solid #fff;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(249,115,22,.35);
  line-height: 1;
}

.contacts-mobile-list{ margin-top:4px; }
.mcard{
  background:var(--card); border-radius:16px;
  border:1px solid var(--border); padding:12px 12px; margin:8px 0;
  box-shadow: 0 10px 26px rgba(2,6,23,.06);
}
.mrow{ display:flex; align-items:center; justify-content:space-between; gap:12px; }
.mline{ height:1px; background:var(--border); margin:10px 0; }
.small-muted{ font-size:.78rem; color:var(--muted); }

.contacts-resumo-card{
  margin-top:12px; background:var(--card); border-radius:18px;
  border:1px solid var(--border); padding:12px 14px 14px; box-shadow: var(--shadow);
}
.contacts-resumo-title{ font-weight:650; font-size:.98rem; margin-bottom:8px; color:var(--text); }
.contacts-resumo-table{ width:100%; border-collapse:collapse; font-size:.82rem; }
.contacts-resumo-table th,
.contacts-resumo-table td{ border:1px solid var(--border); padding:.42rem .45rem; }
.contacts-resumo-table thead{ background:#f8fafc; }

.pretty-modal .modal-content{
  border-radius:20px;
  border:1px solid rgba(226,232,240,.9);
  box-shadow: 0 24px 80px rgba(2,6,23,.20);
  overflow:hidden;
}
.pretty-modal .modal-header{
  border-bottom:1px solid rgba(226,232,240,.9);
  background: radial-gradient(1200px 300px at 20% 0%, rgba(96,81,255,.16), transparent 60%),
              radial-gradient(900px 260px at 90% 0%, rgba(124,58,237,.12), transparent 55%),
              #ffffff;
}
.pretty-modal .modal-title{ font-weight:700; color:#0f172a; }

/* FULLSCREEN DAILY MODAL */
.daily-fullscreen .modal-dialog {
  max-width: 100vw !important;
  width: 100vw !important;
  margin: 0 !important;
  height: 100vh !important;
  max-height: 100vh !important;
}
.daily-fullscreen .modal-content {
  height: 100vh !important;
  max-height: 100vh !important;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
  border: none !important;
}
.daily-fullscreen .modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}
.daily-fullscreen .modal-header {
  border-radius: 0 !important;
  padding: 14px 20px;
  flex-shrink: 0;
}
.daily-fullscreen .modal-footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(226,232,240,.9);
  padding: 10px 20px;
}

/* DAILY CONTACT CARD */
.daily-contact-card {
  background: #fff;
  border: 1.5px solid var(--border);
  border-radius: 18px;
  margin-bottom: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(2,6,23,.06);
  transition: box-shadow .15s;
}
.daily-contact-card:hover { box-shadow: 0 8px 30px rgba(96,81,255,.10); }
.daily-card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
}
.daily-card-img {
  width: 58px;
  height: 58px;
  border-radius: 14px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1.5px solid var(--border);
}
.daily-card-img-placeholder {
  width: 58px;
  height: 58px;
  border-radius: 14px;
  background: linear-gradient(135deg,#f1f5f9,#e2e8f0);
  border: 1.5px dashed #cbd5e1;
  display: grid;
  place-items: center;
  color: #94a3b8;
  font-size: 1.4rem;
  flex-shrink: 0;
}
.daily-card-info { flex: 1; min-width: 0; }
.daily-card-name { font-size: .95rem; font-weight: 800; color: var(--text); margin-bottom: 4px; }
.daily-card-meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 6px; }
.daily-card-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0; }
.daily-card-divider { height: 1px; background: var(--border); }
.daily-card-footer {
  padding: 10px 16px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  background: rgba(248,250,252,.7);
}
.daily-img-field {
  flex: 1;
  min-width: 180px;
}
.daily-img-label {
  font-size: .72rem;
  font-weight: 700;
  color: var(--muted);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.daily-img-input {
  width: 100%;
  border: 1.5px solid var(--border);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: .78rem;
  color: #334155;
  outline: none;
  transition: border-color .15s;
  background: #fff;
}
.daily-img-input:focus { border-color: var(--primary); }
.daily-status-field {
  min-width: 180px;
}
.daily-status-label {
  font-size: .72rem;
  font-weight: 700;
  color: var(--muted);
  margin-bottom: 4px;
}

/* IMAGE UPLOAD STATUS */
.img-upload-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.img-upload-status {
  font-size: .72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
}
.img-upload-status.uploading {
  background: rgba(245,158,11,.12);
  color: #f59e0b;
  border: 1px solid rgba(245,158,11,.25);
}
.img-upload-status.success {
  background: rgba(34,197,94,.12);
  color: #22c55e;
  border: 1px solid rgba(34,197,94,.25);
}
.img-upload-status.error {
  background: rgba(239,68,68,.12);
  color: #ef4444;
  border: 1px solid rgba(239,68,68,.25);
}
.img-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  border: none;
  font-size: .74rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .12s;
  white-space: nowrap;
}
.img-upload-btn:hover { opacity: .88; }
.img-upload-btn:disabled { opacity: .5; cursor: not-allowed; }

/* DAILY SEARCH & FILTER BAR */
.daily-filter-bar {
  background: linear-gradient(180deg, rgba(96,81,255,.04), rgba(96,81,255,.02));
  border: 1px solid rgba(96,81,255,.12);
  border-radius: 14px;
  padding: 10px 14px;
  margin-bottom: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.daily-search-input {
  flex: 1;
  min-width: 180px;
  border: 1.5px solid rgba(96,81,255,.2);
  border-radius: 10px;
  padding: 7px 12px 7px 34px;
  font-size: .82rem;
  color: var(--text);
  outline: none;
  background: #fff;
  transition: border-color .15s;
  font-family: var(--font) !important;
}
.daily-search-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(96,81,255,.08); }
.daily-search-wrap {
  position: relative;
  flex: 1;
  min-width: 180px;
}
.daily-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: .8rem;
  pointer-events: none;
}
.daily-filter-select {
  border: 1.5px solid rgba(96,81,255,.2);
  border-radius: 10px;
  padding: 7px 12px;
  font-size: .82rem;
  color: var(--text);
  outline: none;
  background: #fff;
  font-family: var(--font) !important;
  font-weight: 600;
  cursor: pointer;
  min-width: 160px;
  transition: border-color .15s;
}
.daily-filter-select:focus { border-color: var(--primary); }

/* DAILY NOTES INLINE */
.daily-notes-section {
  border-top: 1px solid rgba(96,81,255,.10);
  background: linear-gradient(180deg,#fafbff,#f5f6ff);
}
.daily-notes-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  cursor: pointer;
  user-select: none;
  transition: background .12s;
}
.daily-notes-toggle:hover { background: rgba(96,81,255,.04); }
.daily-notes-toggle-label {
  font-size: .78rem;
  font-weight: 700;
  color: var(--primary);
  display: flex;
  align-items: center;
  gap: 6px;
}
.daily-notes-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  border-radius: 999px;
  background: var(--primary);
  color: #fff;
  font-size: .68rem;
  font-weight: 800;
  padding: 0 5px;
}

/* PDF SUMMARY COMPACT */
.pdf-summary-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.pdf-summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: .76rem;
  font-weight: 700;
  border: 1.5px solid;
}

/* Trabalho diário */
.daily-pill{
  display:inline-flex; align-items:center; gap:8px;
  border-radius:999px; padding:.32rem .7rem;
  border:1px solid rgba(96,81,255,.18);
  background: rgba(96,81,255,.10); color:#4338ca;
  font-weight:700; font-size:.78rem;
}

.img-url-row{
  display:flex; align-items:center; gap:6px; margin-top:4px;
}
.img-url-input{
  flex:1; font-size:.75rem; padding:3px 8px;
  border:1px solid #e6eaf2; border-radius:8px;
  outline:none; color:#334155;
}
.img-url-input:focus{ border-color: rgba(96,81,255,.45); }
.img-preview{
  width:38px; height:38px; border-radius:8px;
  object-fit:cover; border:1px solid #e6eaf2;
  flex-shrink:0;
}
.img-placeholder{
  width:38px; height:38px; border-radius:8px;
  background:#f1f5f9; border:1px dashed #cbd5e1;
  display:grid; place-items:center; color:#94a3b8;
  flex-shrink:0; font-size:1rem;
}

/* Detail grid */
.detail-grid{
  display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:10px;
}
@media (max-width: 680px){ .detail-grid{ grid-template-columns: 1fr; } }
.detail-item{
  border:1px solid rgba(226,232,240,.9);
  background: rgba(248,250,252,.75);
  border-radius:16px; padding:10px 12px;
}
.detail-label{ font-size:.74rem; color:var(--muted); font-weight:650; margin-bottom:2px; }
.detail-value{ font-size:.9rem; color:var(--text); font-weight:600; word-break:break-word; }

.dropdown-toggle.btn{ border-radius:999px !important; }

/* EDITABLE FIELD */
.editable-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.editable-input {
  border: none;
  border-bottom: 2px solid rgba(96,81,255,.35);
  background: transparent;
  outline: none;
  font-size: inherit;
  font-weight: inherit;
  color: var(--text);
  padding: 1px 4px;
  border-radius: 0;
  font-family: var(--font) !important;
  transition: border-color .15s;
}
.editable-input:focus { border-bottom-color: var(--primary); }
.editable-icon {
  color: var(--muted);
  cursor: pointer;
  font-size: .8rem;
  opacity: .65;
  transition: opacity .12s;
}
.editable-icon:hover { opacity: 1; color: var(--primary); }

/* REPORT NOTES TEXTAREA */
.report-notes-area {
  border: 1.5px solid rgba(96,81,255,.22);
  border-radius: 14px;
  overflow: hidden;
  margin-top: 12px;
}
.report-notes-list {
  max-height: 260px;
  overflow-y: auto;
  padding: 10px 14px;
}
.report-note-item {
  padding: 8px 0;
  border-bottom: 1px solid rgba(226,232,240,.7);
}
.report-note-item:last-child { border-bottom: none; }
.report-note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.report-note-author { font-size: .74rem; font-weight: 700; color: var(--text); }
.report-note-date { font-size: .72rem; color: var(--muted); }
.report-note-text {
  font-size: .84rem;
  color: #334155;
  line-height: 1.55;
  white-space: pre-wrap;
}
.report-note-new {
  border-top: 1.5px solid rgba(96,81,255,.15);
  padding: 12px 14px;
  background: rgba(96,81,255,.03);
}
.report-note-new-label {
  font-size: .74rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.report-note-textarea {
  width: 100%;
  border: 1.5px solid rgba(96,81,255,.25);
  border-radius: 10px;
  padding: 8px 12px;
  font-size: .82rem;
  font-family: var(--font) !important;
  color: var(--text);
  resize: vertical;
  min-height: 70px;
  outline: none;
  background: #fff;
  transition: border-color .15s;
  line-height: 1.55;
}
.report-note-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(96,81,255,.10); }

/* IMAGE FILTER CHIP */
.img-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: .74rem;
  font-weight: 700;
  border: 1.5px solid rgba(96,81,255,.2);
  background: #fff;
  color: var(--muted);
  cursor: pointer;
  transition: all .12s;
}
.img-filter-chip.active {
  background: rgba(96,81,255,.1);
  color: var(--primary);
  border-color: rgba(96,81,255,.3);
}

/* =====================
   PIPELINE KANBAN — custom JS slider, zero deps
   4.5 columns visible (peek signals more to scroll)
   max-height 850px per column, overflow-y auto
===================== */

/* Wrapper — max 915px, never wider, centers itself */
.pk-wrap {
  width: 100%;
  max-width: 1250px;
  position: relative;
  user-select: none;
  overflow-x:scroll;
}

/* Nav row: arrows aligned right */
.pk-nav-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

/* Arrow buttons */
.pk-arrow {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1.5px solid rgba(96,81,255,.22);
  background: #fff;
  color: #6051ff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .12s, border-color .12s, box-shadow .12s;
  box-shadow: 0 2px 8px rgba(96,81,255,.07);
}
.pk-arrow:hover:not(:disabled) {
  background: rgba(96,81,255,.08);
  border-color: rgba(96,81,255,.4);
}
.pk-arrow:disabled {
  opacity: .28;
  cursor: not-allowed;
}

/* Viewport clips the sliding track */
.pk-viewport {
  overflow: hidden;
  width: 100%;
}
.pk-viewport.grabbing { cursor: grabbing; }

/* Track holds all column slides */
.pk-track {
  display: flex;
  gap: 12px;
  transition: transform .32s cubic-bezier(.4,0,.2,1);
  will-change: transform;
  align-items: flex-start;
}
.pk-track.no-transition { transition: none; }

/* Each slide wrapper — width set inline */
.pk-slide { flex-shrink: 0; }

/* Dot indicators */
.pk-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 14px;
  flex-wrap: wrap;
}
.pk-dot {
  height: 8px;
  border-radius: 999px;
  background: #cbd5e1;
  cursor: pointer;
  transition: background .15s, width .2s cubic-bezier(.4,0,.2,1);
  width: 8px;
  flex-shrink: 0;
  border: none;
  padding: 0;
}
.pk-dot.active {
  background: #6051ff;
  width: 22px;
}

/* ── Column — matches design: white bg, subtle shadow, top accent bar ── */
.pipeline-col {
  width: 100%;
  max-height: 850px;
  min-height: 300px;
  background: #fff;
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  transition: border-color .15s, box-shadow .15s;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(15,23,42,.06);
}
.pipeline-col.drag-over {
  border-color: rgba(96,81,255,.4);
  box-shadow: 0 0 0 3px rgba(96,81,255,.10);
}
.pipeline-col-header {
  flex-shrink: 0;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fff;
  border-radius: 12px 12px 0 0;
}
.pipeline-col-title {
  font-size: .8rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 7px;
}
.pipeline-col-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.pipeline-col-count {
  font-size: .7rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  background: #f1f5f9;
  color: #64748b;
  border: none;
}
.pipeline-col-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 800px;
}
.pipeline-col-body::-webkit-scrollbar { width: 3px; }
.pipeline-col-body::-webkit-scrollbar-track { background: transparent; }
.pipeline-col-body::-webkit-scrollbar-thumb { background: rgba(96,81,255,.15); border-radius: 999px; }
.pipeline-col-empty {
  border: 1.5px dashed #e2e8f0;
  border-radius: 10px;
  padding: 28px 10px;
  text-align: center;
  font-size: .72rem;
  font-weight: 600;
  color: #94a3b8;
}

/* ── Kanban Card — matches design ── */
.kcard {
  background: #fff;
  border: 1px solid #e8ecf3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(15,23,42,.05);
  overflow: hidden;
  cursor: grab;
  transition: box-shadow .15s, border-color .15s, transform .1s;
  user-select: none;
  position: relative;
  min-height:270px;
}
.kcard:hover { box-shadow: 0 4px 14px rgba(15,23,42,.09); border-color: #d1d5db; }
.kcard.dragging { opacity: .45; transform: scale(.97) rotate(.8deg); cursor: grabbing; box-shadow: 0 8px 24px rgba(96,81,255,.18); }
.kcard::before {
  content: ""; position: absolute;
  left: 0; top: 0; bottom: 0; width: 3px;
  border-radius: 10px 0 0 10px;
  background: var(--kcard-accent, #6051ff);
}
.kcard-img { width: 100%; height: 88px; object-fit: cover; border-bottom: 1px solid #e8ecf3; display: block; }
.kcard-tags { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; padding: 10px 12px 0 14px; }
.kcard-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 6px; font-size: .64rem; font-weight: 700; }
.kcard-body { padding: 7px 12px 8px 14px; }
.kcard-name { font-size: .86rem; font-weight: 800; color: #0f172a; margin-bottom: 3px; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.kcard-sub { font-size: .72rem; color: #64748b; font-weight: 500; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kcard-commercial { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.kcard-commercial-avatar { width: 20px; height: 20px; border-radius: 6px; object-fit: cover; flex-shrink: 0; }
.kcard-commercial-initials { width: 20px; height: 20px; border-radius: 6px; display: grid; place-items: center; font-size: .58rem; font-weight: 800; color: #fff; flex-shrink: 0; }
.kcard-commercial-name { font-size: .71rem; font-weight: 600; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kcard-footer { padding: 7px 12px 9px 14px; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.kcard-detail-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 8px; font-size: .7rem; font-weight: 700; border: 1.5px solid rgba(96,81,255,.18); background: rgba(96,81,255,.06); color: var(--primary); cursor: pointer; transition: background .12s; white-space: nowrap; }
.kcard-detail-btn:hover { background: rgba(96,81,255,.12); }
.kcard-notes-btn { display: inline-flex; align-items: center; gap: 4px; padding: 5px 9px; border-radius: 8px; font-size: .7rem; font-weight: 700; border: 1.5px solid #e2e8f0; background: #fff; color: #64748b; cursor: pointer; transition: background .12s, color .12s; position: relative; }
.kcard-notes-btn:hover { background: rgba(96,81,255,.05); color: var(--primary); }
.kcard-notes-btn.active { background: rgba(96,81,255,.08); color: var(--primary); border-color: rgba(96,81,255,.22); }
.kcard-notes-panel { border-top: 1px solid rgba(96,81,255,.1); background: linear-gradient(180deg,#fafbff,#f5f6ff); animation: slideDown .18s cubic-bezier(.4,0,.2,1); }

/* ── Pipeline toolbar ── */
.pipeline-toolbar { margin-bottom: 8px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.pipeline-search-wrap { position: relative; flex: 1; min-width: 150px; max-width: 280px; }
.pipeline-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--muted); pointer-events: none; }
.pipeline-search-input { width: 100%; border: 1.5px solid rgba(96,81,255,.2); border-radius: 999px; padding: 6px 12px 6px 30px; font-size: .8rem; color: var(--text); outline: none; background: #fff; transition: border-color .15s; font-family: var(--font) !important; }
.pipeline-search-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(96,81,255,.07); }
.pipeline-summary { display: flex; flex-wrap: wrap; gap: 5px; align-items: center; padding: 6px 0 10px; }
.pipeline-summary-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 7px; font-size: .7rem; font-weight: 700; cursor: pointer; transition: opacity .12s, box-shadow .12s; border: 1.5px solid transparent; white-space: nowrap; }
.pipeline-summary-chip:hover { opacity: .82; }
.pipeline-summary-chip.active-chip { box-shadow: 0 0 0 2px currentColor; }
`;

/* =========================
   PDF TEMPLATE
========================= */

const buildPDFTemplate = ({
  title = "Relatório",
  subtitle = "",
  todayLabel = "",
  sectionsHtml = "",
  authorName = "",
  coverUrl = "https://ik.imagekit.io/zks5iegia/1.png",
  footerUrl = "https://ik.imagekit.io/zks5iegia/9.png",
}) => {
  return `
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
    *{ margin:0; padding:0; box-sizing:border-box; }
    body{
      font-family:'Plus Jakarta Sans', sans-serif; font-size:11.5px; color:#0f172a;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    #pdf-root{ width:794px; }
    .cover{ width:794px; height:1123px; background:url('${coverUrl}') center/cover no-repeat; }
    .footer{
      width:794px; height:1123px;
      background:url('${footerUrl}') center/cover no-repeat;
      page-break-before: always; page-break-inside: avoid;
      break-inside: avoid; display:block; overflow:hidden;
    }
    @page { margin: 25mm 22mm 25mm 22mm; }
    .content{ width:794px; padding:60px 80px 60px 80px; }
    .report-header {
      margin-bottom: 28px;
      padding-bottom: 18px;
      border-bottom: 2px solid rgba(96,81,255,.15);
    }
    .report-author-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(96,81,255,.1);
      border: 1px solid rgba(96,81,255,.2);
      border-radius: 999px;
      padding: 4px 12px;
      font-size: 10px;
      font-weight: 700;
      color: #4338ca;
      margin-bottom: 10px;
    }
    .title{ font-size:23px; font-weight:900; color:#6051ff; margin-bottom:6px; letter-spacing:.2px; }
    .subtitle{ font-size:12px; font-weight:700; color:#334155; margin-bottom:8px; }
    .proposal-date{ font-size:11px; font-weight:600; color:#64748b; }
    .section{ margin-top:24px; page-break-inside: avoid; break-inside: avoid; }
    .section-title{ font-size:14px; font-weight:800; color:#6051ff; margin-bottom:12px; }
    .card{
      padding:14px 14px; background:#f5f6ff;
      border-left:4px solid #6051ff; border-radius:10px;
      border:1px solid rgba(96,81,255,.12);
    }

    .summary-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 4px;
    }
    .s-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 12px;
      border-radius: 999px;
      font-size: 10.5px;
      font-weight: 800;
      border: 1.5px solid;
      white-space: nowrap;
    }
    .s-chip-dot { width: 7px; height: 7px; border-radius: 999px; }

    .grid{ display:grid; grid-template-columns:1fr 1fr; gap:12px 24px; }
    .label{ font-size:10.5px; font-weight:700; color:#64748b; margin-bottom:4px; display:block; }
    .value{ font-size:12px; font-weight:700; color:#0f172a; word-break:break-word; }
    .rtable{
      width:100%; border-collapse:collapse; border:1px solid #e6eaf2;
      border-radius:10px; overflow:hidden; background:#fff;
    }
    .rtable th{
      text-align:left; padding:10px 10px; background:#f8fafc;
      border-bottom:1px solid #e6eaf2; color:#64748b;
      font-size:10.5px; font-weight:800;
    }
    .rtable td{ padding:10px 10px; border-bottom:1px solid #eef2f7; font-weight:650; vertical-align:top; }
    .badge{
      display:inline-flex; align-items:center; gap:8px;
      padding:7px 10px; border-radius:999px; font-weight:800;
      font-size:10.8px; border:1px solid rgba(0,0,0,.06); white-space:nowrap;
    }
    .dot{ width:8px; height:8px; border-radius:999px; background:currentColor; }
    .muted{ color:#64748b; font-weight:650; }

    .school-block{
      margin-bottom:28px; page-break-inside: avoid; break-inside: avoid;
      border:1px solid #e6eaf2; border-radius:14px; overflow:hidden; background:#fff;
    }
    .school-header{ display:flex; gap:0; }
    .school-img-col{ width:200px; min-height:160px; flex-shrink:0; background:#f1f5f9; display:flex; align-items:center; justify-content:center; overflow:hidden; }
    .school-img-col img{ width:200px; height:160px; object-fit:cover; display:block; }
    .school-img-placeholder{ width:200px; min-height:160px; background: linear-gradient(135deg,#f1f5f9,#e2e8f0); display:flex; flex-direction:column; align-items:center; justify-content:center; color:#94a3b8; font-size:11px; font-weight:600; gap:6px; }
    .school-data-col{ flex:1; padding:18px 20px; }
    .school-name{ font-size:16px; font-weight:900; color:#0f172a; margin-bottom:6px; }
    .school-meta{ display:grid; grid-template-columns:1fr 1fr; gap:8px 20px; margin-top:8px; }
    .school-meta-item .label{ font-size:9.5px; }
    .school-meta-item .value{ font-size:11.5px;font-weight:bolder; }
    .school-notes{ border-top:1px solid #e6eaf2; padding:14px 20px; background:#fafbff; }
    .school-notes-title{ font-size:11px; font-weight:800; color:#6051ff; margin-bottom:8px; }
    .school-notes-table{ width:100%; border-collapse:collapse; font-size:10.5px; }
    .school-notes-table th{ text-align:left; padding:6px 8px; background:#f1f5f9; border-bottom:1px solid #e6eaf2; color:#64748b; font-weight:700; }
    .school-notes-table td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; font-weight:600; color:#334155; }
    .no-notes{ font-size:10.5px; color:#94a3b8; font-weight:600; }
  </style>

  <div id="pdf-root">
    <div class="cover"></div>

    <div class="content">
      <div class="report-header">
        ${authorName ? `<div class="report-author-pill">✍️ Elaborado por: ${escapeHtml(authorName)}</div>` : ""}
        <div class="title">${escapeHtml(title)}</div>
        ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ""}
        <div class="proposal-date">${escapeHtml(todayLabel)}</div>
      </div>
      ${sectionsHtml}
    </div>

    <div class="footer"></div>
  </div>
  `;
};

const exportHtmlAsPDF = async ({ html, filename = "relatorio.pdf" }) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-100000px";
  container.style.top = "0";
  container.style.width = "794px";
  container.style.zIndex = "-1";
  container.innerHTML = html;
  document.body.appendChild(container);

  const root = container.querySelector("#pdf-root");
  if (!root) {
    document.body.removeChild(container);
    throw new Error("pdf-root não encontrado");
  }

  await new Promise((r) => setTimeout(r, 350));

  await html2pdf()
    .from(root)
    .set({
      margin: 0,
      filename,
      html2canvas: { scale: 2, useCORS: true, allowTaint: false, imageTimeout: 15000, logging: false },
      jsPDF: { unit: "mm", format: "a4" },
      pagebreak: { mode: ["css", "legacy"] },
    })
    .save();

  document.body.removeChild(container);
};

/* =========================
   EDITABLE FIELD
========================= */
const EditableField = ({ value, onChange, placeholder = "Nome…", style = {} }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    return (
      <span className="editable-field">
        <input
          ref={inputRef}
          className="editable-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setDraft(value || ""); } }}
          placeholder={placeholder}
          style={{ minWidth: 120, ...style }}
          autoFocus
        />
        <LuCheck className="editable-icon" style={{ color: "var(--primary)" }} onClick={commit} />
        <LuX className="editable-icon" onClick={() => { setEditing(false); setDraft(value || ""); }} />
      </span>
    );
  }

  return (
    <span className="editable-field" style={style}>
      <span>{draft || <span style={{ color: "#94a3b8" }}>{placeholder}</span>}</span>
      <LuPencil className="editable-icon" onClick={() => setEditing(true)} />
    </span>
  );
};

/* =========================
   MODAL: CONTACT ACCESS
========================= */

const ContactAccessModal = ({ contact }) => {
  const [show, setShow] = useState(false);
  const [users, setUsers] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const loadUsers = async () => {
    try {
      const res = await axios.get(Hoot() + "api/manager-accounts", { withCredentials: true });
      setUsers(res.data || []);
    } catch { toast.error("Erro ao carregar usuários."); }
  };

  const loadViewers = async () => {
    try {
      const res = await axios.get(Hoot() + `contacts/${contact.ed_contact_id}/viewers`, { withCredentials: true });
      setViewers(res.data.data || []);
    } catch { toast.error("Erro ao carregar permissões."); }
  };

  useEffect(() => {
    if (show) { loadUsers(); loadViewers(); }
  }, [show]);

  const toggleUser = (id) => {
    setSelectedUsers((prev) => prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]);
  };

  const grantAccess = async () => {
    if (!selectedUsers.length) return toast.info("Seleciona pelo menos um usuário.");
    try {
      await Promise.all(
        selectedUsers.map((viewer_id) =>
          axios.post(Hoot() + "contacts/grant-view", { contact_id: contact.ed_contact_id, viewer_id }, { withCredentials: true })
        )
      );
      toast.success("Acessos concedidos!");
      setSelectedUsers([]);
      loadViewers();
    } catch { toast.error("Erro ao conceder acessos."); }
  };

  const removeAccess = async (viewerId) => {
    try {
      await axios.delete(Hoot() + `contacts/${contact.ed_contact_id}/remove-viewer/${viewerId}`, { withCredentials: true });
      toast.success("Acesso removido.");
      loadViewers();
    } catch { toast.error("Erro ao remover."); }
  };

  const filteredUsers = users.filter(
    (u) => normalize(u.name).includes(normalize(search)) || normalize(u.email).includes(normalize(search))
  );

  return (
    <>
      <button className="btn-icon" title="Gerir acessos" onClick={() => setShow(true)}><FiUsers /></button>
      <Modal show={show} onHide={() => setShow(false)} size="lg" centered className="pretty-modal">
        <Modal.Header closeButton>
          <Modal.Title>👥 Acesso ao contacto — {contact?.ed_contact_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input className="form-control mb-3" placeholder="Pesquisar usuário..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <div style={{ maxHeight: 250, overflow: "auto", border: "1px solid #e6eaf2", borderRadius: 12, padding: 10 }}>
            {filteredUsers.map((user) => (
              <div key={user.id} className="d-flex align-items-center justify-content-between mb-2">
                <div><strong>{user.name}</strong><div className="small-muted">{user.email}</div></div>
                <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => toggleUser(user.id)} />
              </div>
            ))}
            {!filteredUsers.length && <div className="small-muted">Nenhum usuário encontrado.</div>}
          </div>
          <Button className="mt-3" size="sm" onClick={grantAccess}>Conceder acesso</Button>
          <hr />
          <h6>Usuários com acesso</h6>
          {viewers.length === 0 && <div className="small-muted">Nenhum acesso concedido ainda.</div>}
          {viewers.map((v) => (
            <div key={v.id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
              <div><strong>{v.name}</strong><div className="small-muted">{v.email}</div></div>
              <Button size="sm" variant="outline-danger" onClick={() => removeAccess(v.id)}>Remover</Button>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </>
  );
};

/* =========================
   INLINE NOTES PANEL
========================= */

const InlineNotesPanel = ({ contact, onClose, me, onNotesLoaded }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const fromDate = toISODateOnly(new Date(new Date().setDate(new Date().getDate() - 30)));
  const toDate = toISODateOnly(new Date());

  const load = async () => {
    if (!contact?.ed_contact_id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        Hoot() + `contacts/${contact.ed_contact_id}/notes?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`,
        { withCredentials: true }
      );
      const loaded = res?.data?.data || [];
      setNotes(loaded);
      // Notifica o parent com a contagem
      onNotesLoaded?.(contact.ed_contact_id, loaded.length);
    } catch { setNotes([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [contact?.ed_contact_id]);

  const submitNote = async () => {
    const txt = newNote.trim();
    if (!txt) return;
    setSaving(true);
    try {
      await axios.post(Hoot() + `contacts/${contact.ed_contact_id}/notes`, { description: txt }, { withCredentials: true });
      setNewNote("");
      await load();
      toast.success("Nota guardada!");
    } catch { toast.error("Erro ao guardar nota."); }
    finally { setSaving(false); }
  };

  const authorName = me?.eduall_user_session_name || me?.data?.eduall_user_session_name || "Eu";
  const authorPic = me?.eduall_user_session_picture || me?.data?.eduall_user_session_picture || null;

  return (
    <div className="notes-inline-panel">
      <div className="notes-inline-header">
        <div className="notes-inline-title">
          <LuStickyNote />
          Notas de {safeValue(contact?.ed_contact_name, "Contacto")}
          <span style={{ background: "rgba(96,81,255,.12)", color: "var(--primary)", borderRadius: 999, padding: "1px 8px", fontSize: ".72rem", fontWeight: 800 }}>
            {notes.length}
          </span>
        </div>
        <button className="btn-icon" onClick={onClose} title="Fechar notas" style={{ width: 26, height: 26, borderRadius: 8 }}>
          <LuX size={13} />
        </button>
      </div>

      <div className="notes-list">
        {loading && (
          <div className="d-flex align-items-center gap-2 py-2">
            <Spinner size="sm" animation="border" style={{ color: "var(--primary)" }} />
            <span className="small-muted">A carregar…</span>
          </div>
        )}
        {!loading && notes.length === 0 && (
          <div className="small-muted py-2">Ainda não existem notas nos últimos 30 dias.</div>
        )}
        {notes.map((n) => {
          const who = n.created_by_name || n.created_by || "—";
          const pic = n.created_by_picture || n.picture || null;
          return (
            <div key={n.id} className="note-item">
              {pic ? (
                <img src={pic} alt="u" className="note-avatar" style={{ width: 28, height: 28 }} />
              ) : (
                <div className="note-avatar" style={{ background: stringToColor(who) }}>
                  {(who || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="note-body">
                <div className="note-meta">
                  <span className="note-author">{who}</span>
                  <span className="note-date">{formatDatePT(n.created_at || n.date)}</span>
                </div>
                <div className="note-text">{n.description || <em style={{ opacity: .5 }}>Sem texto</em>}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="note-new-area">
        <div className="note-new-label">
          {authorPic ? (
            <img src={authorPic} alt="eu" style={{ width: 18, height: 18, borderRadius: 6, objectFit: "cover" }} />
          ) : (
            <div style={{ width: 18, height: 18, borderRadius: 6, background: stringToColor(authorName), display: "grid", placeItems: "center", fontSize: ".6rem", fontWeight: 800, color: "#fff" }}>
              {(authorName).charAt(0).toUpperCase()}
            </div>
          )}
          Nova nota
        </div>
        <textarea
          className="note-textarea"
          placeholder="Escreve uma nota… (suporta quebras de linha)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) submitNote(); }}
          rows={3}
        />
        <div className="d-flex align-items-center justify-content-between mt-1">
          <span className="small-muted">Ctrl+Enter para guardar</span>
          <button className="note-send-btn" onClick={submitNote} disabled={saving || !newNote.trim()}>
            {saving ? <Spinner size="sm" animation="border" /> : <><LuSend size={12} /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================
   DAILY IMAGE UPLOAD FIELD
========================= */

const DailyImageField = ({ contact, imageUrls, setImageUrls, onContactImageUpdated }) => {
  const contactId = contact.ed_contact_id;
  const currentUrl = imageUrls?.[contactId] ?? contact?.ed_contact_picture ?? "";
  const [inputVal, setInputVal] = useState(currentUrl);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    const v = imageUrls?.[contactId] ?? contact?.ed_contact_picture ?? "";
    setInputVal(v);
  }, [contactId, contact?.ed_contact_picture]);

  const handleChange = (e) => {
    setInputVal(e.target.value);
    setUploadStatus(null);
  };

  const applyLocalUrl = () => {
    setImageUrls((prev) => ({ ...prev, [contactId]: inputVal }));
  };

  const handleUpload = async () => {
    const url = inputVal.trim();
    if (!url) return toast.info("Cola primeiro um URL de imagem.");
    setUploadStatus("uploading");
    setUploadError("");
    try {
      const cloudinaryUrl = await uploadImageFromUrl(url);
      await axios.post(
        Hoot() + "contacts/update-picture",
        { contact_id: contactId, picture_url: cloudinaryUrl },
        { withCredentials: true }
      );
      setImageUrls((prev) => ({ ...prev, [contactId]: cloudinaryUrl }));
      setInputVal(cloudinaryUrl);
      setUploadStatus("success");
      toast.success("Imagem guardada no Cloudinary e associada à escola!");
      if (onContactImageUpdated) onContactImageUpdated(contactId, cloudinaryUrl);
    } catch (err) {
      console.error(err);
      setUploadStatus("error");
      setUploadError(err?.response?.data?.message || err?.message || "Erro desconhecido");
      toast.error("Erro ao fazer upload da imagem.");
    }
  };

  const displayUrl = imageUrls?.[contactId] ?? contact?.ed_contact_picture ?? "";

  return (
    <div className="daily-img-field">
      <div className="daily-img-label">
        <LuImage size={11} />
        Imagem da escola (URL → Cloudinary)
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="preview"
            style={{ width: 38, height: 38, borderRadius: 10, objectFit: "cover", border: "1.5px solid #e6eaf2", flexShrink: 0 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "#f1f5f9", border: "1.5px dashed #cbd5e1", display: "grid", placeItems: "center", color: "#94a3b8", flexShrink: 0 }}>
            <LuImage size={16} />
          </div>
        )}
        <input
          className="daily-img-input"
          type="url"
          placeholder="https://… cole o URL da imagem"
          value={inputVal}
          onChange={handleChange}
          onBlur={applyLocalUrl}
          onKeyDown={(e) => { if (e.key === "Enter") { applyLocalUrl(); } }}
          style={{ flex: 1 }}
        />
      </div>
      <div className="img-upload-row">
        <button
          className="img-upload-btn"
          onClick={handleUpload}
          disabled={uploadStatus === "uploading" || !inputVal.trim()}
          title="Converter URL em ficheiro e guardar no Cloudinary"
        >
          {uploadStatus === "uploading"
            ? <><LuLoader size={12} style={{ animation: "spin 1s linear infinite" }} /> A fazer upload…</>
            : <><LuUpload size={12} /> Guardar imagem</>
          }
        </button>
        {uploadStatus === "uploading" && (
          <span className="img-upload-status uploading">
            <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
            A processar…
          </span>
        )}
        {uploadStatus === "success" && (
          <span className="img-upload-status success">
            <LuCheck size={12} /> Guardado!
          </span>
        )}
        {uploadStatus === "error" && (
          <span className="img-upload-status error" title={uploadError}>
            <LuX size={12} /> Erro no upload
          </span>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* =========================
   MODAL: TRABALHO DIÁRIO — FULLSCREEN
========================= */

const DailyWorkModal = ({
  show, onHide, dayKey, contactsById,
  dailyIds, setDailyIds,
  handleStatusChange, reloadContacts,
  imageUrls, setImageUrls,
  me,
}) => {
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [openNotesId, setOpenNotesId] = useState(null);
  const [notesCache, setNotesCache] = useState({});
  const [authorName, setAuthorName] = useState(
    () => me?.eduall_user_session_name || me?.data?.eduall_user_session_name || ""
  );

  const [dailySearch, setDailySearch] = useState("");
  const [dailyStatusFilter, setDailyStatusFilter] = useState("all");

  useEffect(() => {
    const n = me?.eduall_user_session_name || me?.data?.eduall_user_session_name || "";
    setAuthorName(n);
  }, [me]);

  const today = new Date().toLocaleDateString("pt-PT");

  const allDailyContacts = useMemo(() => {
    const list = dailyIds.map((id) => contactsById[id]).filter(Boolean);
    const pr = (s) => {
      const n = normalize(s);
      if (n === "parado") return 1;
      if (n === "em andamento" || n === "emandamento") return 2;
      if (n === "contacto" || n === "contactado") return 3;
      if (n === "interessado") return 4;
      if (n === "em uso" || n === "emuso") return 5;
      if (n === "nao atende" || n === "não atende") return 6;
      if (n === "rejeitado") return 7;
      return 99;
    };
    return list.sort((a, b) => pr(a?.ed_contact_status) - pr(b?.ed_contact_status));
  }, [dailyIds, contactsById]);

  const dailyContacts = useMemo(() => {
    const searchNorm = normalize(dailySearch);
    return allDailyContacts.filter((c) => {
      const matchSearch = !dailySearch ||
        normalize(c.ed_contact_name).includes(searchNorm) ||
        normalize(c.ed_contact_comercial).includes(searchNorm) ||
        normalize(c.ed_contact_province).includes(searchNorm);
      const matchStatus = dailyStatusFilter === "all" || normalize(c.ed_contact_status) === normalize(dailyStatusFilter);
      return matchSearch && matchStatus;
    });
  }, [allDailyContacts, dailySearch, dailyStatusFilter]);

  const removeFromDaily = (id) => setDailyIds((prev) => prev.filter((x) => x !== id));
  const clearDaily = () => setDailyIds([]);

  const handleContactImageUpdated = (contactId, newUrl) => {
    reloadContacts?.();
  };

  const toggleNotes = async (contactId) => {
    if (openNotesId === contactId) { setOpenNotesId(null); return; }
    setOpenNotesId(contactId);
    if (!notesCache[contactId]) {
      const from = toISODateOnly(new Date(new Date().setDate(new Date().getDate() - 30)));
      const to = toISODateOnly(new Date());
      try {
        const res = await axios.get(
          Hoot() + `contacts/${contactId}/notes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
          { withCredentials: true }
        );
        setNotesCache((p) => ({ ...p, [contactId]: res?.data?.data || [] }));
      } catch { setNotesCache((p) => ({ ...p, [contactId]: [] })); }
    }
  };

  const loadAllNotesForPDF = async () => {
    setLoadingNotes(true);
    const from = toISODateOnly(new Date(new Date().setDate(new Date().getDate() - 30)));
    const to = toISODateOnly(new Date());
    const result = {};
    await Promise.all(
      allDailyContacts.map(async (c) => {
        try {
          const res = await axios.get(
            Hoot() + `contacts/${c.ed_contact_id}/notes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
            { withCredentials: true }
          );
          result[c.ed_contact_id] = res?.data?.data || [];
        } catch { result[c.ed_contact_id] = []; }
      })
    );
    setLoadingNotes(false);
    return result;
  };

  const statusChips = [
    { label: "Parados", key: "parado", color: "#ef4444" },
    { label: "Em And.", key: "em andamento", color: "#f59e0b" },
    { label: "Contactados", key: "contacto", color: "#22c55e" },
    { label: "Interessados", key: "interessado", color: "#14b8a6" },
    { label: "Em Uso", key: "em uso", color: "#3b82f6" },
    { label: "Não Atende", key: "não atende", color: "#f97316" },
    { label: "Rejeitados", key: "rejeitado", color: "#64748b" },
  ].filter(chip => allDailyContacts.some(c => normalize(c.ed_contact_status) === chip.key || normalize(c.ed_contact_status) === normalize(chip.key)));

  const buildDailyPDF = (notesData) => {
    const total = allDailyContacts.length;
    const countBy = (status) =>
      allDailyContacts.filter((c) => normalize(c.ed_contact_status) === normalize(status)).length;

    const allChips = [
      { label: "Total", count: total, color: "#6051ff" },
      { label: "Parados", count: countBy("parado"), color: "#ef4444" },
      { label: "Em And.", count: countBy("em andamento"), color: "#f59e0b" },
      { label: "Contactados", count: countBy("contacto"), color: "#22c55e" },
      { label: "Interessados", count: countBy("interessado"), color: "#14b8a6" },
      { label: "Em Uso", count: countBy("em uso"), color: "#3b82f6" },
      { label: "Não Atende", count: countBy("não atende"), color: "#f97316" },
      { label: "Rejeitados", count: countBy("rejeitado"), color: "#64748b" },
    ].filter(c => c.count > 0);

    const chipsHtml = allChips.map(c => `
      <span class="s-chip" style="background:${c.color}18;color:${c.color};border-color:${c.color}33;">
        <span class="s-chip-dot" style="background:${c.color};"></span>
        ${escapeHtml(c.label)}: <strong>${c.count}</strong>
      </span>
    `).join("");

    const statusSummary = `
      <div class="section">
        <div class="section-title">Resumo do Dia</div>
        <div class="card">
          <div style="margin-bottom:8px;font-size:11px;color:#64748b;font-weight:600;">Data: <strong style="color:#0f172a;">${escapeHtml(today)}</strong></div>
          <div class="summary-chips">${chipsHtml}</div>
        </div>
      </div>`;

    const schoolBlocks = allDailyContacts.map((c) => {
      const st = c.ed_contact_status || "";
      const hex = getStatusColorHex(st);
      const imgUrl = imageUrls?.[c.ed_contact_id] || c.ed_contact_picture || "";
      const notes = notesData?.[c.ed_contact_id] || [];

      const imgHtml = imgUrl
        ? `<div class="school-img-col"><img src="${escapeHtml(imgUrl)}" alt="escola" crossorigin="anonymous" /></div>`
        : `<div class="school-img-col"><div class="school-img-placeholder"><svg width="32" height="32" fill="none" stroke="#cbd5e1" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="10" width="18" height="11" rx="2"/><path d="M3 10l9-7 9 7"/><path d="M9 21V13h6v8"/></svg><span>Sem imagem</span></div></div>`;

      const notesRows = notes.length > 0
        ? notes.map((n) => `
            <tr>
              <td>${escapeHtml(formatDatePT(n.created_at || n.date))}</td>
              <td>${escapeHtml(n.created_by_name || n.created_by || "—")}</td>
              <td>${escapeHtml(n.description || "")}</td>
            </tr>`).join("")
        : `<tr><td colspan="3" class="no-notes">Sem notas no período (últimos 30 dias).</td></tr>`;

      return `
        <div class="school-block">
          <div class="school-header">
            ${imgHtml}
            <div class="school-data-col">
              <div class="school-name">${escapeHtml(safeValue(c.ed_contact_name, "—"))}</div>
              <span class="badge" style="background:${escapeHtml(hex)}1a;color:${escapeHtml(hex)};border-color:${escapeHtml(hex)}33">
                <span class="dot" style="background:${escapeHtml(hex)}"></span>
                ${escapeHtml(statusLabelPDF(st))}
              </span>
              <div class="school-meta">
                <div class="school-meta-item"><span class="label">Tipo</span><div class="value">${escapeHtml(c.ed_contact_type === 1 ? "Universidade" : "Colégio")}</div></div>
                <div class="school-meta-item"><span class="label">Província</span><div class="value">${escapeHtml(safeValue(c.ed_contact_province || "Luanda"))}</div></div>
                <div class="school-meta-item"><span class="label">Telefone</span><div class="value">${escapeHtml(safeValue(c.ed_contact_phone, "—"))}</div></div>
                <div class="school-meta-item"><span class="label">Comercial</span><div class="value">${escapeHtml(safeValue(c.ed_contact_comercial, "—"))}</div></div>
                <div class="school-meta-item" style="grid-column:1/-1;"><span class="label">Email</span><div class="value">${escapeHtml(safeValue(c.ed_contact_email, "—"))}</div></div>
              </div>
            </div>
          </div>
          <div class="school-notes">
            <div class="school-notes-title">📝 Notas recentes (últimos 30 dias)</div>
            <table class="school-notes-table">
              <thead><tr><th style="width:100px;">Data</th><th style="width:180px;">Por</th><th>Descrição</th></tr></thead>
              <tbody>${notesRows}</tbody>
            </table>
          </div>
        </div>`;
    }).join("");

    const listSection = `
      <div class="section">
        <div class="section-title">Instituições do Dia — Detalhe</div>
        ${schoolBlocks || `<div class="muted">Sem instituições selecionadas.</div>`}
      </div>`;

    return buildPDFTemplate({
      title: "Trabalho Diário",
      subtitle: "Relatório de prospeção / contacto",
      todayLabel: `Data do relatório: ${today}`,
      authorName,
      sectionsHtml: `${statusSummary}${listSection}`,
    });
  };

  const exportDailyPDF = async () => {
    if (!allDailyContacts.length) return toast.info("Ainda não selecionaste nenhuma instituição para hoje.");
    try {
      setLoadingPDF(true);
      const notesData = await loadAllNotesForPDF();
      const html = buildDailyPDF(notesData);
      await exportHtmlAsPDF({ html, filename: `trabalho-diario-${dayKey}.pdf` });
      toast.success("PDF do trabalho diário gerado!");
    } catch (e) {
      console.log(e);
      toast.error("Erro ao gerar PDF do trabalho diário.");
    } finally { setLoadingPDF(false); }
  };

  return (
    <Modal show={show} onHide={() => !loadingPDF && onHide?.()} centered className="pretty-modal daily-fullscreen">
      <Modal.Header closeButton={!loadingPDF}>
        <div className="d-flex align-items-center justify-content-between w-100 pe-3">
          <Modal.Title className="d-flex align-items-center gap-2">
            <LuListChecks style={{ color: "var(--primary)" }} />
            Trabalho Diário — {dayKey}
          </Modal.Title>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="daily-pill">
              <span>Hoje:</span><b>{today}</b>
              <span style={{ opacity: .5 }}>•</span>
              <b>{allDailyContacts.length}</b> escolas
            </div>
            {statusChips.slice(0, 4).map(chip => (
              <span key={chip.key} className="pdf-summary-chip" style={{ background: `${chip.color}14`, color: chip.color, borderColor: `${chip.color}33`, fontSize: ".72rem" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: chip.color, display: "inline-block" }} />
                {allDailyContacts.filter(c => normalize(c.ed_contact_status) === chip.key || normalize(c.ed_contact_status) === normalize(chip.key)).length} {chip.label}
              </span>
            ))}
            <Button variant="outline-danger" size="sm" onClick={clearDaily} disabled={!allDailyContacts.length} style={{ borderRadius: 999, fontSize: ".76rem", padding: "4px 12px" }}>
              <LuTrash2 style={{ marginRight: 4 }} />Limpar
            </Button>
            <Button variant="primary" size="sm" onClick={exportDailyPDF} disabled={loadingPDF || loadingNotes} style={{ borderRadius: 999, fontSize: ".76rem", padding: "4px 14px" }}>
              {loadingPDF || loadingNotes ? <Spinner size="sm" animation="border" /> : <><LuFileDown style={{ marginRight: 4 }} />PDF</>}
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Autor */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div style={{ fontSize: ".78rem", color: "var(--muted)", fontWeight: 650 }}>Elaborado por:</div>
          <EditableField
            value={authorName}
            onChange={setAuthorName}
            placeholder="O teu nome…"
            style={{ fontWeight: 700, fontSize: ".88rem" }}
          />
        </div>

        {/* SEARCH & FILTER BAR */}
        {allDailyContacts.length > 0 && (
          <div className="daily-filter-bar">
            <div className="daily-search-wrap">
              <LuSearch className="daily-search-icon" size={14} />
              <input
                className="daily-search-input"
                type="search"
                placeholder="Pesquisar escola, comercial, província…"
                value={dailySearch}
                onChange={(e) => setDailySearch(e.target.value)}
              />
            </div>
            <select
              className="daily-filter-select"
              value={dailyStatusFilter}
              onChange={(e) => setDailyStatusFilter(e.target.value)}
            >
              <option value="all">Todos os estados</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {(dailySearch || dailyStatusFilter !== "all") && (
              <span className="small-muted">
                {dailyContacts.length} de {allDailyContacts.length} escolas
              </span>
            )}
            {(dailySearch || dailyStatusFilter !== "all") && (
              <button
                onClick={() => { setDailySearch(""); setDailyStatusFilter("all"); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: ".74rem", display: "flex", alignItems: "center", gap: 4 }}
              >
                <LuX size={12} /> Limpar filtros
              </button>
            )}
          </div>
        )}

        {allDailyContacts.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
            <LuListChecks size={40} style={{ opacity: .3, marginBottom: 12 }} />
            <div style={{ fontWeight: 700, fontSize: ".9rem" }}>Nenhuma escola selecionada</div>
            <div style={{ fontSize: ".8rem", marginTop: 4 }}>Vai à listagem e clica no ícone 📌 para adicionar ao Trabalho Diário.</div>
          </div>
        )}

        {allDailyContacts.length > 0 && dailyContacts.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: "var(--muted)" }}>
            <LuSearch size={32} style={{ opacity: .3, marginBottom: 10 }} />
            <div style={{ fontWeight: 700, fontSize: ".88rem" }}>Sem resultados para os filtros aplicados</div>
          </div>
        )}

        {/* Cards das escolas */}
        {dailyContacts.map((c) => {
          const isOpen = openNotesId === c.ed_contact_id;
          const notes = notesCache[c.ed_contact_id] || [];
          const st = c.ed_contact_status || "";
          const hex = getStatusColorHex(st);
          const displayImg = imageUrls?.[c.ed_contact_id] ?? c.ed_contact_picture ?? "";

          return (
            <div key={c.ed_contact_id} className="daily-contact-card">
              <div className="daily-card-header">
                {displayImg ? (
                  <img
                    src={displayImg}
                    alt="escola"
                    className="daily-card-img"
                    onError={(e) => { e.target.style.display = "none"; e.target.nextSibling?.style && (e.target.nextSibling.style.display = "grid"); }}
                  />
                ) : null}
                <div
                  className="daily-card-img-placeholder"
                  style={{ display: displayImg ? "none" : "grid" }}
                >
                  <LuImage />
                </div>

                <div className="daily-card-info">
                  <div className="daily-card-name">{safeValue(c.ed_contact_name, "—")}</div>
                  <div className="daily-card-meta">
                    <span className={`status-badge ${getStatusClass(st)}`} style={{ fontSize: ".72rem", padding: "4px 8px" }}>
                      <span className="status-dot" />
                      {statusLabel(st)}
                    </span>
                    {getTypeBadge(c.ed_contact_type)}
                    <span className="pill"><i className="bi bi-geo-alt-fill" />{safeValue(c.ed_contact_province || "Luanda")}</span>
                    {c.ed_contact_comercial && <span className="pill"><i className="bi bi-person-badge-fill" />{c.ed_contact_comercial}</span>}
                  </div>
                  {c.ed_contact_phone && (
                    <div className="small-muted mt-1"><i className="bi bi-telephone me-1" />{c.ed_contact_phone}</div>
                  )}
                </div>

                <div className="daily-card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => removeFromDaily(c.ed_contact_id)}
                    title="Remover do Trabalho Diário"
                    style={{ color: "#ef4444" }}
                  >
                    <LuTrash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="daily-card-footer">
                <DailyImageField
                  contact={c}
                  imageUrls={imageUrls}
                  setImageUrls={setImageUrls}
                  onContactImageUpdated={handleContactImageUpdated}
                />
                <div className="daily-status-field">
                  <div className="daily-status-label">Estado</div>
                  <select
                    className="form-select form-select-sm rounded-pill"
                    value={st}
                    onChange={(e) => handleStatusChange(c.ed_contact_id, e.target.value)}
                    style={{ fontWeight: 700, borderColor: `${hex}44`, color: hex }}
                  >
                    <option value="">Estado…</option>
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes toggle */}
              <div className="daily-notes-section">
                <div className="daily-notes-toggle" onClick={() => toggleNotes(c.ed_contact_id)}>
                  <div className="daily-notes-toggle-label">
                    <LuStickyNote size={13} />
                    Notas
                    <span className="daily-notes-badge">{isOpen ? (notes.length) : "…"}</span>
                  </div>
                  {isOpen ? <LuChevronUp size={14} style={{ color: "var(--primary)" }} /> : <LuChevronDown size={14} style={{ color: "var(--muted)" }} />}
                </div>
                {isOpen && (
                  <InlineNotesPanel
                    contact={c}
                    me={me}
                    onClose={() => setOpenNotesId(null)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" disabled={loadingPDF} onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

/* =========================
   MODAL: EXPORT RELATÓRIO (PDF)
========================= */

const ExportReportModal = ({
  show, onHide, exportKind,
  selectedCommercial, contact, filteredContacts,
  comercialObj, chartImageDataUrl,
  statusCounts, commercialStatusMap,
  uniqueStatuses, commercialNames,
  me,
}) => {
  const [fromDate, setFromDate] = useState(toISODateOnly(new Date(new Date().setDate(new Date().getDate() - 30))));
  const [toDate, setToDate] = useState(toISODateOnly(new Date()));
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [authorName, setAuthorName] = useState(
    () => me?.eduall_user_session_name || me?.data?.eduall_user_session_name || ""
  );

  const today = new Date().toLocaleDateString("pt-PT");

  useEffect(() => {
    const n = me?.eduall_user_session_name || me?.data?.eduall_user_session_name || "";
    setAuthorName(n);
  }, [me]);

  const loadNotesForContact = async () => {
    if (exportKind !== "contact") return;
    if (!contact?.ed_contact_id) return;
    setLoadingNotes(true);
    try {
      const res = await axios.get(
        Hoot() + `contacts/${contact.ed_contact_id}/notes?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`,
        { withCredentials: true }
      );
      setNotes(res?.data?.data || []);
    } catch { setNotes([]); }
    finally { setLoadingNotes(false); }
  };

  useEffect(() => {
    if (show) { if (exportKind === "contact") loadNotesForContact(); else setNotes([]); }
  }, [show, exportKind, contact?.ed_contact_id]);

  const addNewNote = async () => {
    const txt = newNoteText.trim();
    if (!txt || !contact?.ed_contact_id) return;
    setSavingNote(true);
    try {
      await axios.post(Hoot() + `contacts/${contact.ed_contact_id}/notes`, { description: txt }, { withCredentials: true });
      setNewNoteText("");
      await loadNotesForContact();
      toast.success("Nota adicionada!");
    } catch { toast.error("Erro ao guardar nota."); }
    finally { setSavingNote(false); }
  };

  const orderByStatusPriority = (a, b) => {
    const pr = (s) => {
      const n = normalize(s);
      if (n === "parado") return 1; if (n === "em andamento" || n === "emandamento") return 2;
      if (n === "contacto" || n === "contactado") return 3; if (n === "interessado") return 4;
      if (n === "em uso" || n === "emuso") return 5; if (n === "nao atende" || n === "não atende") return 6;
      if (n === "rejeitado") return 7; return 99;
    };
    return pr(a.ed_contact_status) - pr(b.ed_contact_status);
  };

  const buildReportHtml = () => {
    const subtitle = exportKind === "commercial" ? `Por Comercial • ${selectedCommercial || "—"}`
      : exportKind === "contact" ? `Por Contacto • ${contact?.ed_contact_name || "—"}`
      : "Global • Análise e comparação";

    const headerBlock = (() => {
      if (exportKind === "contact") {
        const name = contact?.ed_contact_name || "Instituição";
        const st = contact?.ed_contact_status || "";
        const color = getStatusColorHex(st);
        return `
          <div class="section">
            <div class="section-title">Identificação</div>
            <div class="card">
              <div class="grid">
                <div><span class="label">Instituição</span><div class="value">${escapeHtml(name)}</div></div>
                <div><span class="label">Estado</span><div class="value">
                  <span class="badge" style="background:${escapeHtml(color)}1a;color:${escapeHtml(color)};border-color:${escapeHtml(color)}33">
                    <span class="dot" style="background:${escapeHtml(color)}"></span>${escapeHtml(statusLabelPDF(st))}
                  </span>
                </div></div>
                <div><span class="label">Período</span><div class="value">${escapeHtml(fromDate)} → ${escapeHtml(toDate)}</div></div>
                <div><span class="label">Gerado em</span><div class="value">${escapeHtml(today)}</div></div>
              </div>
            </div>
          </div>`;
      }
      const name = comercialObj?.name || selectedCommercial || "Comercial";
      const total = statusCounts?.total ?? filteredContacts.length;
      return `
        <div class="section">
          <div class="section-title">Resumo</div>
          <div class="card">
            <div class="grid">
              <div><span class="label">${exportKind === "commercial" ? "Comercial" : "Relatório"}</span>
                <div class="value">${escapeHtml(exportKind === "commercial" ? name : "Global")}</div></div>
              <div><span class="label">Período</span><div class="value">${escapeHtml(fromDate)} → ${escapeHtml(toDate)}</div></div>
              <div><span class="label">Total (filtros atuais)</span><div class="value">${total}</div></div>
              <div><span class="label">Gerado em</span><div class="value">${escapeHtml(today)}</div></div>
            </div>
          </div>
        </div>`;
    })();

    const list = (() => {
      let arr = [...(filteredContacts || [])];
      if (exportKind === "commercial") arr = arr.filter((c) => (c.ed_contact_comercial || "") === (selectedCommercial || ""));
      if (exportKind === "contact") arr = arr.filter((c) => c.ed_contact_id === contact?.ed_contact_id);
      arr.sort(orderByStatusPriority);
      const rows = arr.map((c) => {
        const st = c.ed_contact_status || ""; const hex = getStatusColorHex(st);
        const badge = `<span class="badge" style="background:${escapeHtml(hex)}1a;color:${escapeHtml(hex)};border-color:${escapeHtml(hex)}33"><span class="dot" style="background:${escapeHtml(hex)}"></span>${escapeHtml(statusLabelPDF(st))}</span>`;
        return `<tr>
          <td><div style="font-weight:900;color:#0f172a;">${escapeHtml(safeValue(c.ed_contact_name, "—"))}</div>
            <div class="muted" style="margin-top:2px;">${escapeHtml(safeValue(c.ed_contact_phone, "Sem telefone"))} • ${escapeHtml(safeValue(c.ed_contact_email, "Sem email"))}</div></td>
          <td>${escapeHtml(c.ed_contact_type === 1 ? "Universidade" : "Colégio")}</td>
          <td>${badge}</td>
          <td>${escapeHtml(safeValue(c.ed_contact_province || "Luanda"))}</td>
          <td>${escapeHtml(safeValue(c.ed_contact_comercial, "—"))}</td>
        </tr>`;
      }).join("");
      return `
        <div class="section">
          <div class="section-title">Listagem de Escolas</div>
          <table class="rtable">
            <thead><tr><th>Instituição</th><th style="width:120px;">Tipo</th><th style="width:190px;">Estado</th><th style="width:120px;">Província</th><th style="width:170px;">Comercial</th></tr></thead>
            <tbody>${rows || `<tr><td colspan="5" class="muted">Sem registos.</td></tr>`}</tbody>
          </table>
        </div>`;
    })();

    const notesBlock = (() => {
      if (exportKind !== "contact") return "";
      const rows = notes?.length > 0
        ? notes.map((n) => `<tr><td>${escapeHtml(formatDatePT(n.created_at || n.date))}</td><td>${escapeHtml(n.created_by_name || n.created_by || "—")}</td><td>${escapeHtml(n.description || "")}</td></tr>`).join("")
        : `<tr><td colspan="3" class="muted">Sem notas no período.</td></tr>`;
      return `
        <div class="section">
          <div class="section-title">Notas no Período</div>
          <table class="rtable">
            <thead><tr><th style="width:120px;">Data</th><th style="width:220px;">Criada por</th><th>Descrição</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    })();

    const globalBlock = (() => {
      if (exportKind !== "global") return "";
      const statusesToShow = (uniqueStatuses?.length ? uniqueStatuses : statusOptions.map((s) => s.value)) || [];
      const allChipsGlobal = statusesToShow.map(s => {
        const total = (commercialNames || []).reduce((sum, name) => sum + (commercialStatusMap?.[name]?.[s] || 0), 0);
        const hex = getStatusColorHex(s);
        return total > 0 ? `<span class="s-chip" style="background:${hex}18;color:${hex};border-color:${hex}33;"><span class="s-chip-dot" style="background:${hex};"></span>${escapeHtml(statusLabelPDF(s))}: <strong>${total}</strong></span>` : "";
      }).filter(Boolean).join("");
      const head = statusesToShow.map((s) => `<th>${escapeHtml(statusLabelPDF(s))}</th>`).join("");
      const body = (commercialNames || []).map((name) => {
        const m = commercialStatusMap?.[name] || {};
        const total = Object.values(m).reduce((a, b) => a + b, 0);
        const cols = statusesToShow.map((st) => {
          const c = m?.[st] || 0;
          const pct = total > 0 ? ((c / total) * 100).toFixed(1) : "0.0";
          return `<td>${c} / ${pct}%</td>`;
        }).join("");
        return `<tr><td style="font-weight:900;">${escapeHtml(name)}</td><td>${total}</td>${cols}</tr>`;
      }).join("");
      return `
        <div class="section">
          <div class="section-title">Comparativo por Comercial</div>
          <div class="card" style="margin-bottom:14px;">
            <div class="summary-chips">${allChipsGlobal}</div>
          </div>
          ${chartImageDataUrl
            ? `<img src="${escapeHtml(chartImageDataUrl)}" alt="chart" style="width:100%;border-radius:12px;border:1px solid #e6eaf2;margin-bottom:12px;" />`
            : `<div class="muted">Gráfico não disponível.</div>`}
          <table class="rtable">
            <thead><tr><th style="width:220px;">Comercial</th><th style="width:70px;">Total</th>${head}</tr></thead>
            <tbody>${body || `<tr><td colspan="${2 + statusesToShow.length}" class="muted">Sem dados.</td></tr>`}</tbody>
          </table>
        </div>`;
    })();

    const summaryBlock = (() => {
      const total = statusCounts?.total ?? filteredContacts.length;
      const parado = statusCounts?.parado ?? 0;
      const emuso = statusCounts?.em_uso ?? 0;
      const interessado = statusCounts?.interessado ?? 0;
      const chips = [
        { label: "Total", count: total, color: "#6051ff" },
        { label: "Parados", count: parado, color: "#ef4444" },
        { label: "Interessados", count: interessado, color: "#14b8a6" },
        { label: "Em uso", count: emuso, color: "#3b82f6" },
      ].filter(c => c.count > 0).map(c =>
        `<span class="s-chip" style="background:${c.color}18;color:${c.color};border-color:${c.color}33;"><span class="s-chip-dot" style="background:${c.color};"></span>${escapeHtml(c.label)}: <strong>${c.count}</strong></span>`
      ).join("");
      return `
        <div class="section">
          <div class="section-title">Resumo rápido</div>
          <div class="card">
            <div class="summary-chips" style="margin-bottom:10px;">${chips}</div>
            <div class="muted">Sugestão: regista sempre uma nota curta (chamada, WhatsApp, visita, email).</div>
          </div>
        </div>`;
    })();

    const sections = `${headerBlock}${summaryBlock}${list}${notesBlock}${globalBlock}`;
    return buildPDFTemplate({
      title: "Relatório de Contactos",
      subtitle,
      todayLabel: `Data do relatório: ${today}`,
      authorName,
      sectionsHtml: sections,
    });
  };

  const exportPDF = async () => {
    if (exportKind === "commercial" && (!selectedCommercial || selectedCommercial === "all")) {
      toast.error("Seleciona um comercial específico para exportar o relatório."); return;
    }
    if (exportKind === "contact" && !contact?.ed_contact_id) {
      toast.error("Seleciona um contacto para exportar o relatório."); return;
    }
    try {
      setLoading(true);
      if (exportKind === "contact") await loadNotesForContact();
      const filenameBase = exportKind === "commercial"
        ? `relatorio-comercial-${(selectedCommercial || "comercial").replace(/\s+/g, "_")}`
        : exportKind === "contact"
        ? `relatorio-contacto-${(contact?.ed_contact_name || "contacto").replace(/\s+/g, "_")}`
        : "relatorio-global-eduall";
      const html = buildReportHtml();
      await exportHtmlAsPDF({ html, filename: `${filenameBase}.pdf` });
      toast.success("PDF gerado com sucesso!");
      onHide?.();
    } catch (e) { console.log(e); toast.error("Erro ao gerar PDF."); }
    finally { setLoading(false); }
  };

  const title = exportKind === "commercial" ? "Exportar relatório — por comercial"
    : exportKind === "contact" ? "Exportar relatório — por contacto"
    : "Exportar relatório — global";

  return (
    <Modal show={show} onHide={() => !loading && onHide?.()} centered size="lg" className="pretty-modal">
      <Modal.Header closeButton={!loading}>
        <Modal.Title><LuDownload style={{ marginRight: 8, color: "var(--primary)" }} />{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-2 mb-3 p-2" style={{ background: "rgba(96,81,255,.05)", borderRadius: 12, border: "1px solid rgba(96,81,255,.12)" }}>
          <span style={{ fontSize: ".74rem", fontWeight: 700, color: "var(--muted)" }}>✍️ Elaborado por:</span>
          <EditableField
            value={authorName}
            onChange={setAuthorName}
            placeholder="O teu nome…"
            style={{ fontWeight: 700, fontSize: ".88rem" }}
          />
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <div className="detail-label">Período (de)</div>
            <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="detail-item">
            <div className="detail-label">Período (até)</div>
            <input className="form-control form-control-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
            <div className="detail-label">Resumo</div>
            <div className="detail-value">
              {exportKind === "commercial" && (<>Comercial: <b>{safeValue(selectedCommercial, "—")}</b><br />Contactos: <b>{filteredContacts?.length ?? 0}</b></>)}
              {exportKind === "contact" && (<>Instituição: <b>{safeValue(contact?.ed_contact_name, "—")}</b><br />Estado: <b>{statusLabel(contact?.ed_contact_status)}</b></>)}
              {exportKind === "global" && (<>Filtros atuais aplicados<br />Total: <b>{filteredContacts?.length ?? 0}</b></>)}
            </div>
          </div>
        </div>

        {exportKind === "contact" && contact?.ed_contact_id && (
          <div className="report-notes-area mt-3">
            <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid rgba(96,81,255,.12)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: ".8rem", fontWeight: 750, color: "var(--primary)", display: "flex", alignItems: "center", gap: 6 }}>
                <LuStickyNote size={13} />
                Notas no período
                <span style={{ background: "rgba(96,81,255,.12)", color: "var(--primary)", borderRadius: 999, padding: "1px 7px", fontSize: ".7rem", fontWeight: 800 }}>
                  {notes.length}
                </span>
              </div>
              <Button size="sm" variant="outline-secondary" onClick={loadNotesForContact} disabled={loadingNotes} style={{ fontSize: ".72rem", padding: "2px 10px" }}>
                {loadingNotes ? <Spinner size="sm" animation="border" /> : "Atualizar"}
              </Button>
            </div>

            <div className="report-notes-list">
              {loadingNotes && (
                <div className="d-flex align-items-center gap-2 py-2">
                  <Spinner size="sm" animation="border" style={{ color: "var(--primary)" }} />
                  <span className="small-muted">A carregar notas…</span>
                </div>
              )}
              {!loadingNotes && notes.length === 0 && (
                <div className="small-muted py-2">Sem notas neste período.</div>
              )}
              {notes.map((n) => (
                <div key={n.id} className="report-note-item">
                  <div className="report-note-header">
                    <span className="report-note-author">{n.created_by_name || n.created_by || "—"}</span>
                    <span className="report-note-date">{formatDatePT(n.created_at || n.date)}</span>
                  </div>
                  <div className="report-note-text">{n.description || <em style={{ opacity: .4 }}>Sem texto</em>}</div>
                </div>
              ))}
            </div>

            <div className="report-note-new">
              <div className="report-note-new-label">
                <LuPlus size={12} /> Adicionar nova nota (aparece no PDF)
              </div>
              <textarea
                className="report-note-textarea"
                placeholder="Escreve uma nota… (suporta quebras de linha)"
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) addNewNote(); }}
                rows={3}
              />
              <div className="d-flex align-items-center justify-content-between mt-1">
                <span className="small-muted">Ctrl+Enter para guardar</span>
                <button className="note-send-btn" onClick={addNewNote} disabled={savingNote || !newNoteText.trim()}>
                  {savingNote ? <Spinner size="sm" animation="border" /> : <><LuSend size={12} /> Guardar nota</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" disabled={loading} onClick={onHide}>Cancelar</Button>
        <Button variant="primary" size="sm" disabled={loading} onClick={exportPDF}>
          {loading ? <Spinner size="sm" animation="border" /> : "Gerar PDF"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* =========================
   PIPELINE KANBAN COMPONENT
========================= */

const PipelineKanban = ({ contacts, comerciais, handleStatusChange, imageUrls, me, onOpenDetails }) => {
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [dragContactId, setDragContactId]   = useState(null);
  const [dragOverCol, setDragOverCol]       = useState(null);
  const [openNotesId, setOpenNotesId]       = useState(null);
  const [notesCountMap, setNotesCountMap]   = useState({});
  // current index = index of the leftmost visible column
  const [offset, setOffset]   = useState(0);
  const viewportRef  = useRef(null);
  const wrapRef      = useRef(null);
  const isDraggingSlider = useRef(false);
  const dragStartX = useRef(0);
  const dragStartOffset = useRef(0);

  const columns      = statusOptions;
  const VISIBLE      = 4;
  const PEEK         = 0.5;    // half-column peek — shows edge of next column
  const GAP          = 12;     // px gap between columns

  // colWidth is measured from the real DOM — starts at 0, set after mount
  const [colWidth, setColWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const calcWidth = () => {
      const vw = el.offsetWidth || 0;
      if (!vw) return;
      const totalGap = GAP * (VISIBLE + PEEK - 1);
      setColWidth(Math.floor((vw - totalGap) / (VISIBLE + PEEK)));
    };
    calcWidth();
    const ro = new ResizeObserver(calcWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const MAX_OFFSET = Math.max(0, columns.length - VISIBLE);

  /* ── Slider helpers ── */
  const clampOffset = v => Math.max(0, Math.min(MAX_OFFSET, v));

  // Use colWidth state; fallback to a safe default until measured
  const effectiveColWidth = colWidth > 0 ? colWidth : 200;

  const getTranslate = (off) => -(off * (effectiveColWidth + GAP));

  /* arrow nav */
  const goPrev = () => setOffset(o => clampOffset(o - 1));
  const goNext = () => setOffset(o => clampOffset(o + 1));
  const goTo   = idx => setOffset(clampOffset(idx));

  /* ── Mouse drag on viewport ── */
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    isDraggingSlider.current = true;
    dragStartX.current = e.clientX;
    dragStartOffset.current = offset;
    viewportRef.current?.classList.add("grabbing");
  };
  const onMouseMove = (e) => {
    if (!isDraggingSlider.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX.current;
    const delta = -dx / (effectiveColWidth + GAP);
    setOffset(clampOffset(dragStartOffset.current + delta));
  };
  const onMouseUp = (e) => {
    if (!isDraggingSlider.current) return;
    isDraggingSlider.current = false;
    viewportRef.current?.classList.remove("grabbing");
    // snap to nearest column
    setOffset(o => Math.round(clampOffset(o)));
  };

  /* ── Touch swipe on viewport ── */
  const touchStartX = useRef(null);
  const touchStartOffset = useRef(0);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartOffset.current = offset;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const delta = -dx / (effectiveColWidth + GAP);
    setOffset(clampOffset(touchStartOffset.current + delta));
  };
  const onTouchEnd = () => {
    touchStartX.current = null;
    setOffset(o => Math.round(clampOffset(o)));
  };

  /* ── Kanban drag & drop ── */
  const matchCol = (status, colValue) => {
    const s  = normalize(status);
    const cv = normalize(colValue);
    if (cv === s) return true;
    if (cv === "contacto"     && (s === "contactado"  || s === "contacto"))    return true;
    if (cv === "em andamento" && (s === "emandamento" || s === "em andamento")) return true;
    if (cv === "em uso"       && (s === "emuso"       || s === "em uso"))      return true;
    if (cv === "não atende"   && (s === "nao atende"  || s === "não atende"))  return true;
    return false;
  };

  const filtered = useMemo(() => {
    const norm = normalize(pipelineSearch);
    if (!norm) return contacts;
    return contacts.filter(c =>
      normalize(c.ed_contact_name).includes(norm) ||
      normalize(c.ed_contact_comercial).includes(norm) ||
      normalize(c.ed_contact_province).includes(norm)
    );
  }, [contacts, pipelineSearch]);

  const byStatus = useMemo(() => {
    const map = {};
    columns.forEach(col => { map[col.value] = []; });
    filtered.forEach(c => {
      const col = columns.find(col => matchCol(c.ed_contact_status, col.value));
      if (col) map[col.value].push(c);
    });
    return map;
  }, [filtered]);

  const handleDragStart = (e, id) => { setDragContactId(id); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver  = (e, v)  => { e.preventDefault(); setDragOverCol(v); };
  const handleDrop      = async (e, colValue) => {
    e.preventDefault(); setDragOverCol(null);
    if (!dragContactId) return;
    const c = contacts.find(x => x.ed_contact_id === dragContactId);
    if (!c || matchCol(c.ed_contact_status, colValue)) return;
    await handleStatusChange(dragContactId, colValue);
    setDragContactId(null);
  };
  const handleDragEnd = () => { setDragContactId(null); setDragOverCol(null); };

  const toggleNotes       = id => setOpenNotesId(p => p === id ? null : id);
  const handleNotesLoaded = (id, count) => setNotesCountMap(p => ({ ...p, [id]: count }));

  /* ── Determine snapped offset for dots highlight ── */
  const snappedOffset = Math.round(offset);

  /* ── KanbanCard ── */
  const KanbanCard = ({ c, accentColor }) => {
    const displayImg = imageUrls?.[c.ed_contact_id] ?? c.ed_contact_picture ?? "";
    const isOpen     = openNotesId === c.ed_contact_id;
    const notesCount = notesCountMap[c.ed_contact_id];
    const isDragging = dragContactId === c.ed_contact_id;
    const comObj     = (comerciais || []).find(x => x?.name === c.ed_contact_comercial) || null;
    const comPic     = comObj?.picture || comObj?.photo || comObj?.com_picture || null;

    return (
      <div
        className={`kcard${isDragging ? " dragging" : ""}`}
        style={{ "--kcard-accent": accentColor }}
        draggable
        onDragStart={e => handleDragStart(e, c.ed_contact_id)}
        onDragEnd={handleDragEnd}
      >
        {!!displayImg && (
          <img src={displayImg} alt="escola" className="kcard-img"
            onError={e => { e.target.style.display = "none"; }} />
        )}
        <div className="kcard-tags">
          <span className="kcard-tag" style={{
            background: c.ed_contact_type === 1 ? "rgba(245,158,11,.12)" : "rgba(99,102,241,.10)",
            color:      c.ed_contact_type === 1 ? "#b45309" : "#4338ca",
          }}>
            <i className={`bi ${c.ed_contact_type === 1 ? "bi-mortarboard-fill" : "bi-building"}`} style={{ fontSize: ".6rem" }} />
            {c.ed_contact_type === 1 ? "Universidade" : "Colégio"}
          </span>
          {c.ed_contact_province && (
            <span className="kcard-tag" style={{ background: "#f1f5f9", color: "#475569" }}>
              <i className="bi bi-geo-alt-fill" style={{ fontSize: ".6rem" }} />
              {c.ed_contact_province}
            </span>
          )}
        </div>
        <div className="kcard-body">
          <div className="kcard-name">{safeValue(c.ed_contact_name, "Sem nome")}</div>
          {c.ed_contact_phone && (
            <div className="kcard-sub">
              <i className="bi bi-telephone-fill me-1" style={{ fontSize: ".62rem" }} />
              {c.ed_contact_phone}
            </div>
          )}
          {c.ed_contact_comercial && (
            <div className="kcard-commercial">
              {comPic
                ? <img src={comPic} alt="com" className="kcard-commercial-avatar" onError={e => e.target.style.display = "none"} />
                : <div className="kcard-commercial-initials" style={{ background: stringToColor(c.ed_contact_comercial) }}>
                    {c.ed_contact_comercial.charAt(0).toUpperCase()}
                  </div>
              }
              <span className="kcard-commercial-name">{c.ed_contact_comercial}</span>
            </div>
          )}
        </div>
        <div className="kcard-footer">
          <button className="kcard-detail-btn" onClick={() => onOpenDetails(c)}>
            <LuScanEye size={11} /> Detalhes
          </button>
          <div style={{ position: "relative", display: "inline-flex" }}>
            <button
              className={`kcard-notes-btn${isOpen ? " active" : ""}`}
              onClick={() => toggleNotes(c.ed_contact_id)}
            >
              <LuStickyNote size={11} />
              {notesCount > 0 ? notesCount : "Notas"}
            </button>
            {notesCount > 0 && <span className="notes-count-badge">{notesCount}</span>}
          </div>
        </div>
        {isOpen && (
          <div className="kcard-notes-panel">
            <InlineNotesPanel contact={c} me={me} onClose={() => setOpenNotesId(null)} onNotesLoaded={handleNotesLoaded} />
          </div>
        )}
      </div>
    );
  };

  /* ── Column ── */
  const renderColumn = (col) => {
    const colCards   = byStatus[col.value] || [];
    const colColor   = getStatusColorHex(col.value);
    const isDragOver = dragOverCol === col.value;
    return (
      <div
        className={`pipeline-col${isDragOver ? " drag-over" : ""}`}
        style={{ borderTop: `3px solid ${colColor}` }}
        onDragOver={e => handleDragOver(e, col.value)}
        onDrop={e => handleDrop(e, col.value)}
        onDragLeave={() => setDragOverCol(null)}
      >
        <div className="pipeline-col-header">
          <div className="pipeline-col-title">
            <span className="pipeline-col-dot" style={{ background: colColor }} />
            {col.label}
          </div>
          <span
            className="pipeline-col-count"
            style={{ background: `${colColor}15`, color: colColor }}
          >
            {colCards.length}
          </span>
        </div>
        <div className="pipeline-col-body">
          {colCards.length === 0 && <div className="pipeline-col-empty">Sem contactos</div>}
          {colCards.map(c => (
            <KanbanCard key={c.ed_contact_id} c={c} accentColor={colColor} />
          ))}
        </div>
      </div>
    );
  };

  /* ── Translate value (may be fractional during drag) ── */
  const translateX = getTranslate(offset);
  const isTransitioning = !isDraggingSlider.current && touchStartX.current === null;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Toolbar */}
      <div className="pipeline-toolbar">
        <div className="pipeline-search-wrap">
          <LuSearch className="pipeline-search-icon" size={13} />
          <input
            className="pipeline-search-input"
            type="search"
            placeholder="Pesquisar no pipeline…"
            value={pipelineSearch}
            onChange={e => setPipelineSearch(e.target.value)}
          />
        </div>
        <span className="small-muted">{filtered.length} contacto{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Summary chips */}
      <div className="pipeline-summary">
        {columns.map((col, idx) => {
          const count    = (byStatus[col.value] || []).length;
          const color    = getStatusColorHex(col.value);
          const isActive = snappedOffset === idx;
          return (
            <span
              key={col.value}
              className={`pipeline-summary-chip${isActive ? " active-chip" : ""}`}
              style={{ background: `${color}12`, color, borderColor: `${color}${isActive ? "66" : "22"}` }}
              onClick={() => goTo(idx)}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />
              {col.label}
              <b style={{ marginLeft: 2, opacity: .85 }}>{count}</b>
            </span>
          );
        })}
      </div>

      {/* Board — wrapRef is ALWAYS here, never moves */}
      <div ref={wrapRef} className="pk-wrap">

        {/* Nav arrows */}
        <div className="pk-nav-row">
          <button className="pk-arrow" disabled={snappedOffset === 0}         onClick={goPrev} aria-label="Anterior"><IoChevronBackOutline    size={15} /></button>
          <button className="pk-arrow" disabled={snappedOffset >= MAX_OFFSET} onClick={goNext} aria-label="Próximo" ><IoChevronForwardOutline size={15} /></button>
        </div>

        {/* While width is not yet measured show a placeholder that preserves layout */}
        {colWidth === 0 ? (
          <div
            className="pk-viewport"
            style={{ minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#94a3b8", fontSize: ".8rem" }}
          >
            <LuLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
            A carregar pipeline…
          </div>
        ) : (
          /* Real board once colWidth is known */
          <div
            ref={viewportRef}
            className="pk-viewport"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className={`pk-track${isTransitioning ? "" : " no-transition"}`}
              style={{ transform: `translateX(${translateX}px)` }}
            >
              {columns.map(col => (
                <div
                  key={col.value}
                  className="pk-slide"
                  style={{ width: effectiveColWidth, minWidth: 190 }}
                >
                  {renderColumn(col)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dots */}
        {colWidth > 0 && (
          <div className="pk-dots">
            {Array.from({ length: MAX_OFFSET + 1 }).map((_, i) => (
              <button
                key={i}
                className={`pk-dot${snappedOffset === i ? " active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Ir para posição ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* =========================
   MAIN: ContactsListTable
========================= */

const ContactsListTable = () => {
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState("listagem");
  const [search, setSearch] = useState("");
  const [selectedCommercial, setSelectedCommercial] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedProvince, setSelectedProvince] = useState("all");
  const [selectedImageFilter, setSelectedImageFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [comerciais, setComerciais] = useState([]);
  const [loadingComerciais, setLoadingComerciais] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsContact, setDetailsContact] = useState(null);

  const [openInlineNotesId, setOpenInlineNotesId] = useState(null);
  // Mapa de contactId → contagem de notas (populado quando o painel de notas é aberto)
  const [notesCountMap, setNotesCountMap] = useState({});

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportKind, setExportKind] = useState("commercial");

  const [me, setMe] = useState(null);

  const chartWrapRef = useRef(null);
  const [chartImageDataUrl, setChartImageDataUrl] = useState("");

  // TRABALHO DIÁRIO
  // Usamos uma chave fixa (sem data) para que os dados persistam entre refreshes do mesmo dia.
  // O utilizador só pode remover contactos individualmente ou limpar tudo manualmente.
  const DAILY_STORAGE_KEY = "daily-work-contacts";
  const IMAGE_URLS_STORAGE_KEY = "daily-image-urls";

  const dayKey = useMemo(() => toISODateOnly(new Date()), []);

  const [showDailyModal, setShowDailyModal] = useState(false);
  const [dailyIds, setDailyIds] = useState(() => {
    // Inicialização lazy a partir do localStorage — garante que sobrevive a refresh
    try {
      const raw = localStorage.getItem(DAILY_STORAGE_KEY);
      return raw ? JSON.parse(raw) || [] : [];
    } catch { return []; }
  });

  const [imageUrls, setImageUrls] = useState(() => {
    // Inicialização lazy dos URLs de imagem
    try {
      const raw = localStorage.getItem(IMAGE_URLS_STORAGE_KEY);
      return raw ? JSON.parse(raw) || {} : {};
    } catch { return {}; }
  });

  useEffect(() => {
    axios.get(Hoot() + "me", { withCredentials: true })
      .then(r => setMe(r.data || null))
      .catch(() => setMe(null));
  }, []);

  // Persiste dailyIds no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(DAILY_STORAGE_KEY, JSON.stringify(dailyIds));
    } catch {}
  }, [dailyIds]);

  // Persiste imageUrls no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(IMAGE_URLS_STORAGE_KEY, JSON.stringify(imageUrls));
    } catch {}
  }, [imageUrls]);

  const loadContacts = async () => {
    try {
      const res = await axios.get(Hoot() + "contacts/list", { withCredentials: true });
      setContacts(res.data.data || []);
    } catch (err) {
      console.clear(); console.log(err);
      toast.error("Erro ao carregar contactos.");
    }
  };

  const loadComerciais = async () => {
    setLoadingComerciais(true);
    try {
      const res = await axios.get(Hoot() + "comerciais?limit=200&page=1", { withCredentials: true });
      setComerciais(res?.data?.data || []);
    } catch (err) {
      console.log(err); toast.error("Erro ao carregar comerciais."); setComerciais([]);
    } finally { setLoadingComerciais(false); }
  };

  useEffect(() => { loadContacts(); loadComerciais(); }, []);

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await axios.post(Hoot() + "contacts/update-status", { contact_id: contactId, contact_status: newStatus }, { withCredentials: true });
      toast.success("Estado actualizado.");
      loadContacts();
    } catch { toast.error("Erro ao atualizar estado."); }
  };

  const openDetails = (contact) => { setDetailsContact(contact); setShowDetailsModal(true); };

  const toggleInlineNotes = (contactId) => {
    setOpenInlineNotesId((prev) => prev === contactId ? null : contactId);
  };

  // Callback chamado pelo InlineNotesPanel quando carrega as notas
  const handleNotesLoaded = (contactId, count) => {
    setNotesCountMap((prev) => ({ ...prev, [contactId]: count }));
  };

  const uniqueStatuses = useMemo(
    () => Array.from(new Set(contacts.map((c) => c.ed_contact_status).filter(Boolean))),
    [contacts]
  );

  const apiCommercialNames = useMemo(
    () => Array.from(new Set((comerciais || []).map((c) => c?.name).filter(Boolean))),
    [comerciais]
  );

  const fallbackCommercialNames = useMemo(
    () => Array.from(new Set(contacts.map((c) => c.ed_contact_comercial).filter(Boolean))),
    [contacts]
  );

  const selectCommercials = useMemo(
    () => (apiCommercialNames.length ? apiCommercialNames : fallbackCommercialNames),
    [apiCommercialNames, fallbackCommercialNames]
  );

  const filtered = useMemo(() => {
    const searchNorm = normalize(search);
    return contacts.filter((c) => {
      const matchesSearch =
        normalize(c.ed_contact_name).includes(searchNorm) ||
        normalize(c.ed_contact_phone).includes(searchNorm) ||
        normalize(c.ed_contact_email).includes(searchNorm) ||
        normalize(c.ed_contact_address).includes(searchNorm) ||
        normalize(c.ed_contact_comercial).includes(searchNorm);
      const matchesCommercial = selectedCommercial === "all" || (c.ed_contact_comercial || "") === selectedCommercial;
      const matchesStatus = selectedStatus === "all" || c.ed_contact_status === selectedStatus;
      const matchesProvince = selectedProvince === "all" || normalize(c.ed_contact_province) === normalize(selectedProvince);
      const hasImage = !!(c.ed_contact_picture && c.ed_contact_picture.trim());
      const matchesImage =
        selectedImageFilter === "all" ||
        (selectedImageFilter === "with" && hasImage) ||
        (selectedImageFilter === "without" && !hasImage);
      return matchesSearch && matchesCommercial && matchesStatus && matchesProvince && matchesImage;
    });
  }, [contacts, search, selectedCommercial, selectedStatus, selectedProvince, selectedImageFilter]);

  const statusCounts = useMemo(() => {
    const counts = { total: filtered.length, parado: 0, contacto: 0, em_andamento: 0, em_uso: 0, rejeitado: 0, nao_atende: 0, interessado: 0 };
    filtered.forEach((c) => {
      const s = normalize(c.ed_contact_status);
      if (s === "parado") counts.parado++;
      else if (s === "contacto" || s === "contactado") counts.contacto++;
      else if (s === "em andamento" || s === "emandamento") counts.em_andamento++;
      else if (s === "em uso" || s === "emuso") counts.em_uso++;
      else if (s === "rejeitado") counts.rejeitado++;
      else if (s === "nao atende" || s === "não atende") counts.nao_atende++;
      else if (s === "interessado") counts.interessado++;
    });
    return counts;
  }, [filtered]);

  const pageCount = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const commercialStatusMap = useMemo(() => {
    const map = {};
    filtered.forEach((c) => {
      const name = c.ed_contact_comercial || "N/A";
      const status = c.ed_contact_status || "indefinido";
      if (!map[name]) map[name] = {};
      if (!map[name][status]) map[name][status] = 0;
      map[name][status]++;
    });
    return map;
  }, [filtered]);

  const commercialNames = useMemo(() => Object.keys(commercialStatusMap), [commercialStatusMap]);

  const chartSeries = useMemo(() => {
    const statuses = uniqueStatuses.length ? uniqueStatuses : statusOptions.map((s) => s.value);
    return statuses.map((status) => ({
      name: statusLabel(status),
      data: commercialNames.map((name) => commercialStatusMap[name]?.[status] || 0),
      color: getStatusColorHex(status),
    }));
  }, [uniqueStatuses, commercialNames, commercialStatusMap]);

  const chartOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false }, height: 340 },
    plotOptions: { bar: { borderRadius: 8, columnWidth: "46%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: commercialNames, labels: { rotate: -30 } },
    legend: { position: "bottom", labels: { colors: "#334155" } },
    grid: { borderColor: "#e2e8f0" },
    tooltip: { theme: "light", y: { formatter: (val) => `${val} contactos` } },
  }), [commercialNames]);

  const exportContactsToCSV = () => {
    if (selectedCommercial === "all") { toast.error("Seleciona um comercial específico para exportar."); return; }
    const filteredContacts = contacts.filter((c) => (c.ed_contact_comercial || "") === selectedCommercial);
    if (!filteredContacts.length) return toast.info("Nenhum contacto encontrado para este comercial.");
    const headers = ["Instituição", "Tipo", "Estado", "Província", "Comercial", "Telefone", "Email", "Endereço"];
    const rows = filteredContacts.map((c) => [
      c.ed_contact_name, c.ed_contact_type === 1 ? "Universidade" : "Colégio",
      c.ed_contact_status, c.ed_contact_province || "Luanda",
      c.ed_contact_comercial, c.ed_contact_phone, c.ed_contact_email, c.ed_contact_address,
    ]);
    const csvContent = [headers, ...rows]
      .map((e) => e.map((f) => `"${(f || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `contactos-${selectedCommercial.replace(/\s+/g, "_").toLowerCase()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exportação CSV concluída com sucesso.");
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 7;
    if (pageCount <= maxButtons) {
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(pageCount - 1, currentPage + 1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < pageCount - 1) pages.push("...");
      pages.push(pageCount);
    }
    return pages.map((p, idx) =>
      p === "..." ? (
        <li key={`ellipsis-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
      ) : (
        <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
          <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
        </li>
      )
    );
  };

  const StatusBadge = ({ status }) => {
    const cls = getStatusClass(status);
    return (
      <span className={`status-badge ${cls}`}>
        <span className="status-dot" />
        {safeValue(statusLabel(status), "Sem estado")}
      </span>
    );
  };

  const CommercialMini = ({ name, province, picture }) => (
    <div className="d-flex align-items-center gap-2">
      {picture ? (
        <img src={picture} alt="c" style={{ width: 28, height: 28, borderRadius: 10, objectFit: "cover" }} />
      ) : (
        <div className="contacts-avatar" style={{ background: stringToColor(name || "N") }}>
          {(name || "N").charAt(0).toUpperCase()}
        </div>
      )}
      <div>
        <div style={{ fontSize: ".82rem", fontWeight: 600, color: "#0f172a" }}>{safeValue(name, "N/A")}</div>
        <div className="small-muted">{safeValue(province || "Luanda")}</div>
      </div>
    </div>
  );

  const selectedCommercialObj = useMemo(() => {
    if (!selectedCommercial || selectedCommercial === "all") return null;
    return (comerciais || []).find((c) => c?.name === selectedCommercial) || null;
  }, [comerciais, selectedCommercial]);

  useEffect(() => {
    const tryCapture = async () => {
      try {
        const wrap = chartWrapRef.current;
        if (!wrap) return;
        const svg = wrap.querySelector("svg");
        if (!svg) return;
        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width * 2; canvas.height = img.height * 2;
          const ctx = canvas.getContext("2d");
          ctx.scale(2, 2); ctx.drawImage(img, 0, 0);
          setChartImageDataUrl(canvas.toDataURL("image/png"));
          URL.revokeObjectURL(url);
        };
        img.src = url;
      } catch { setChartImageDataUrl(""); }
    };
    setTimeout(tryCapture, 700);
  }, [commercialNames, chartSeries]);

  const openExport = (kind) => { setExportKind(kind); setShowExportModal(true); };

  const contactsById = useMemo(() => {
    const map = {};
    (contacts || []).forEach((c) => { map[c.ed_contact_id] = c; });
    return map;
  }, [contacts]);

  const toggleDaily = (contactId) => {
    setDailyIds((prev) => prev.includes(contactId) ? prev.filter((x) => x !== contactId) : [...prev, contactId]);
  };

  const imageFilterCounts = useMemo(() => {
    const all = contacts.length;
    const withImg = contacts.filter(c => !!(c.ed_contact_picture && c.ed_contact_picture.trim())).length;
    const withoutImg = all - withImg;
    return { all, with: withImg, without: withoutImg };
  }, [contacts]);

  return (
    <>
      <style>{contactsStyles}</style>

      <div className="contacts-shell">
        <div className="contacts-card">
          {/* HEADER */}
          <div className="contacts-header">
            <div className="contacts-header-title">
              <i className="bi bi-buildings-fill" style={{ color: "var(--primary)" }} />
              <span>Contactos</span>
              <span className="contacts-chip">
                <i className="bi bi-people-fill" />{filtered.length} contactos
              </span>
              <span className="daily-pill" title="Instituições selecionadas para hoje">
                <LuListChecks />Hoje: <b>{dailyIds.length}</b>
              </span>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <RegisterOrUpdateContactModal
                toggle_btn={<button className="btn btn-outline-primary btn-sm rounded-pill">+ Adicionar contacto</button>}
              />

              <button
                className="btn btn-primary btn-sm rounded-pill d-flex align-items-center"
                onClick={() => setShowDailyModal(true)}
                title="Abrir Trabalho Diário"
              >
                <LuListChecks style={{ marginRight: 6 }} />
                Trabalho diário
                <span className="ms-2 badge bg-light text-dark">{dailyIds.length}</span>
              </button>

              <Dropdown>
                <Dropdown.Toggle variant="outline-dark" size="sm" className="d-flex align-items-center">
                  <LuDownload style={{ marginRight: 6 }} />Relatórios (PDF)
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => openExport("commercial")}>PDF — Por Comercial</Dropdown.Item>
                  <Dropdown.Item onClick={() => { if (!detailsContact) toast.info("Abre os detalhes de um contacto (👁️) para exportar."); openExport("contact"); }}>
                    PDF — Do Contacto (com notas)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => openExport("global")}>PDF — Global (com gráfico)</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <button
                className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center"
                onClick={exportContactsToCSV}
                disabled={selectedCommercial === "all"}
              >
                <i className="bi bi-filetype-csv me-1" />CSV (por comercial)
              </button>

              <button
                className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center"
                onClick={() => { loadContacts(); loadComerciais(); }}
              >
                <i className="bi bi-arrow-clockwise me-1" />Recarregar
              </button>
            </div>
          </div>

          {/* TOOLBAR */}
          <div className="contacts-toolbar">
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <input type="search" className="form-control form-control-sm rounded-pill"
                  placeholder="Pesquisar por instituição, telefone, e-mail, comercial…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="col-6 col-md-2">
                <select className="form-select form-select-sm rounded-pill"
                  value={selectedCommercial}
                  onChange={(e) => { setSelectedCommercial(e.target.value); setCurrentPage(1); }}
                  disabled={loadingComerciais}
                >
                  <option value="all">{loadingComerciais ? "A carregar..." : "Todos os comerciais"}</option>
                  {selectCommercials.map((name) => (<option key={name} value={name}>{name}</option>))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <select className="form-select form-select-sm rounded-pill"
                  value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">Todos os estados</option>
                  {statusOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <select className="form-select form-select-sm rounded-pill"
                  value={selectedProvince}
                  onChange={(e) => { setSelectedProvince(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">Todas as províncias</option>
                  {provinciasList.map((prov) => (<option key={prov} value={prov}>{prov}</option>))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <select
                  className="form-select form-select-sm rounded-pill"
                  value={selectedImageFilter}
                  onChange={(e) => { setSelectedImageFilter(e.target.value); setCurrentPage(1); }}
                  style={{
                    borderColor: selectedImageFilter !== "all" ? "rgba(96,81,255,.35)" : "rgba(226,232,240,.9)",
                    fontWeight: selectedImageFilter !== "all" ? 700 : 500,
                    color: selectedImageFilter !== "all" ? "var(--primary)" : "inherit",
                  }}
                >
                  <option value="all">🖼️ Todas as imagens ({imageFilterCounts.all})</option>
                  <option value="with">Com imagem ({imageFilterCounts.with})</option>
                  <option value="without">Sem imagem ({imageFilterCounts.without})</option>
                </select>
              </div>
              <div className="col-6 col-md-2 d-flex align-items-center justify-content-md-end">
                <small className="small-muted">Página <b>{currentPage}</b> de <b>{pageCount}</b></small>
              </div>
            </div>
            {selectedImageFilter !== "all" && (
              <div className="mt-2 d-flex align-items-center gap-2">
                <span
                  className="img-filter-chip active"
                  onClick={() => { setSelectedImageFilter("all"); setCurrentPage(1); }}
                  title="Clica para remover este filtro"
                >
                  <LuImage size={12} />
                  {selectedImageFilter === "with" ? "Com imagem" : "Sem imagem"}
                  <span style={{ marginLeft: 2, opacity: .6 }}>{filtered.length}</span>
                  <LuX size={10} />
                </span>
              </div>
            )}
          </div>

          {/* KPIs */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-title"><i className="bi bi-collection" /> Total (filtro)</div>
              <div className="kpi-value">{statusCounts.total}</div>
              <div className="kpi-sub">Todos os contactos encontrados</div>
            </div>
            <div className="kpi">
              <div className="kpi-title" style={{ color: "#ef4444" }}><i className="bi bi-pause-circle-fill" /> Parados</div>
              <div className="kpi-value">{statusCounts.parado}</div>
              <div className="kpi-sub">Sem avanço</div>
            </div>
            <div className="kpi">
              <div className="kpi-title" style={{ color: "#f59e0b" }}><i className="bi bi-hourglass-split" /> Em andamento</div>
              <div className="kpi-value">{statusCounts.em_andamento}</div>
              <div className="kpi-sub">Em progresso</div>
            </div>
            <div className="kpi">
              <div className="kpi-title" style={{ color: "#22c55e" }}><i className="bi bi-telephone-outbound-fill" /> Contactados</div>
              <div className="kpi-value">{statusCounts.contacto}</div>
              <div className="kpi-sub">Já houve contacto</div>
            </div>
            <div className="kpi">
              <div className="kpi-title" style={{ color: "#14b8a6" }}><i className="bi bi-stars" /> Interessados</div>
              <div className="kpi-value">{statusCounts.interessado}</div>
              <div className="kpi-sub">Maior probabilidade</div>
            </div>
            <div className="kpi">
              <div className="kpi-title" style={{ color: "#3b82f6" }}><i className="bi bi-check2-circle" /> Em uso</div>
              <div className="kpi-value">{statusCounts.em_uso}</div>
              <div className="kpi-sub">Já a usar</div>
            </div>
          </div>

          {/* TABS */}
          <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k || "listagem"); setCurrentPage(1); }} className="mb-2">
            <Tab eventKey="listagem" title="Listagem de contactos">
              <div className="contacts-table-wrapper">
                {/* Desktop */}
                <div className="contacts-table-desktop">
                  <table className="contacts-table">
                    <thead>
                      <tr>
                        <th>Instituição</th><th>Tipo</th><th>Estado</th>
                        <th>Província</th><th>Comercial</th><th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((c) => {
                        const comObj = (comerciais || []).find((x) => x?.name === c.ed_contact_comercial) || null;
                        const pic = comObj?.picture || comObj?.photo || comObj?.com_picture || null;
                        const inDaily = dailyIds.includes(c.ed_contact_id);
                        const isNotesOpen = openInlineNotesId === c.ed_contact_id;
                        // Contagem de notas para este contacto (se já carregada)
                        const notesCount = notesCountMap[c.ed_contact_id];
                        return (
                          <React.Fragment key={c.ed_contact_id}>
                            <tr>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {c?.ed_contact_picture ? (
                                    <img
                                      src={c.ed_contact_picture}
                                      alt="avatar"
                                      className="contacts-avatar"
                                      style={{ objectFit: "cover" }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling && (e.target.nextSibling.style.display = "inline-grid");
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className="contacts-avatar"
                                    style={{
                                      background: stringToColor(c?.ed_contact_name || "?"),
                                      display: c?.ed_contact_picture ? "none" : "inline-grid",
                                    }}
                                  >
                                    {(c?.ed_contact_name || "?").charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 600 }}>{safeValue(c.ed_contact_name, "Sem nome")}</div>
                                    <div className="small-muted"><i className="bi bi-telephone me-1" />{safeValue(c.ed_contact_phone, "Sem telefone")}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{getTypeBadge(c.ed_contact_type)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <StatusBadge status={c.ed_contact_status} />
                                  <select value={c.ed_contact_status || ""}
                                    onChange={(e) => handleStatusChange(c.ed_contact_id, e.target.value)}
                                    className="form-select form-select-sm rounded-pill"
                                    style={{ width: 170, borderColor: "rgba(226,232,240,.9)", fontWeight: 600 }}
                                  >
                                    <option value="">Estado…</option>
                                    {statusOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                                  </select>
                                </div>
                              </td>
                              <td><span className="pill"><i className="bi bi-geo-alt-fill" />{safeValue(c.ed_contact_province || "Luanda")}</span></td>
                              <td><CommercialMini name={c.ed_contact_comercial} province={c.ed_contact_province} picture={pic} /></td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <button className="btn-icon" type="button"
                                    onClick={() => toggleDaily(c.ed_contact_id)}
                                    title={inDaily ? "Remover do Trabalho Diário" : "Adicionar ao Trabalho Diário"}
                                    style={{ background: inDaily ? "rgba(96,81,255,.10)" : "#fff", borderColor: inDaily ? "rgba(96,81,255,.22)" : "rgba(226,232,240,.95)" }}
                                  >
                                    {inDaily ? <LuBookmarkCheck /> : <LuBookmarkPlus />}
                                  </button>
                                  <ContactAccessModal contact={c} />
                                  <button className="btn-icon" type="button" onClick={() => openDetails(c)} title="Ver detalhes"><LuScanEye /></button>
                                  {/* Botão de notas com badge laranja se tiver contagem */}
                                  <div className="notes-btn-wrapper">
                                    <button
                                      className="btn-icon"
                                      type="button"
                                      onClick={() => toggleInlineNotes(c.ed_contact_id)}
                                      title="Notas"
                                      style={{
                                        background: isNotesOpen ? "rgba(96,81,255,.10)" : "#fff",
                                        borderColor: isNotesOpen ? "rgba(96,81,255,.22)" : "rgba(226,232,240,.95)",
                                      }}
                                    >
                                      <LuStickyNote />
                                    </button>
                                    {notesCount > 0 && (
                                      <span className="notes-count-badge">{notesCount}</span>
                                    )}
                                  </div>
                                  <RegisterOrUpdateContactModal contactId={c.ed_contact_id} update={true}
                                    toggle_btn={<button className="btn-icon" type="button" title="Editar contacto"><FiEdit3 /></button>}
                                  />
                                </div>
                              </td>
                            </tr>
                            {isNotesOpen && (
                              <tr className="notes-inline-row">
                                <td colSpan={6}>
                                  <InlineNotesPanel
                                    contact={c}
                                    me={me}
                                    onClose={() => setOpenInlineNotesId(null)}
                                    onNotesLoaded={handleNotesLoaded}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {paginated.length === 0 && (
                        <tr><td colSpan={6}><span className="small-muted">Nenhum contacto encontrado para os filtros actuais.</span></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile */}
                <div className="contacts-mobile-list">
                  {paginated.map((c) => {
                    const inDaily = dailyIds.includes(c.ed_contact_id);
                    const isNotesOpen = openInlineNotesId === c.ed_contact_id;
                    const notesCount = notesCountMap[c.ed_contact_id];
                    return (
                      <div className="mcard" key={c.ed_contact_id}>
                        <div className="mrow">
                          <div className="d-flex align-items-center gap-2">
                            {c?.ed_contact_picture ? (
                              <img
                                src={c.ed_contact_picture}
                                alt="avatar"
                                className="contacts-avatar"
                                style={{ objectFit: "cover" }}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <div className="contacts-avatar" style={{ background: stringToColor(c.ed_contact_name || "?") }}>
                                {(c.ed_contact_name || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 650 }}>{safeValue(c.ed_contact_name, "Sem nome")}</div>
                              <div className="small-muted">{safeValue(c.ed_contact_province || "Luanda")}</div>
                            </div>
                          </div>
                          <StatusBadge status={c.ed_contact_status} />
                        </div>
                        <div className="mline" />
                        <div className="d-flex flex-wrap gap-2">
                          {getTypeBadge(c.ed_contact_type)}
                          <span className="pill"><i className="bi bi-person-badge-fill" />{safeValue(c.ed_contact_comercial, "Sem comercial")}</span>
                          <span className="pill"><i className="bi bi-telephone-fill" />{safeValue(c.ed_contact_phone, "Sem telefone")}</span>
                        </div>
                        <div className="mline" />
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="small-muted">Ações</small>
                          <div className="d-flex gap-2">
                            <button className="btn-icon"
                              onClick={() => toggleDaily(c.ed_contact_id)}
                              style={{ background: inDaily ? "rgba(96,81,255,.10)" : "#fff", borderColor: inDaily ? "rgba(96,81,255,.22)" : "rgba(226,232,240,.95)" }}
                            >
                              {inDaily ? <LuBookmarkCheck /> : <LuBookmarkPlus />}
                            </button>
                            <button className="btn-icon" onClick={() => openDetails(c)}><i className="bi bi-eye" /></button>
                            {/* Botão de notas mobile com badge */}
                            <div className="notes-btn-wrapper">
                              <button className="btn-icon"
                                onClick={() => toggleInlineNotes(c.ed_contact_id)}
                                style={{ background: isNotesOpen ? "rgba(96,81,255,.10)" : "#fff", borderColor: isNotesOpen ? "rgba(96,81,255,.22)" : "rgba(226,232,240,.95)" }}
                              >
                                <i className="bi bi-card-text" />
                              </button>
                              {notesCount > 0 && (
                                <span className="notes-count-badge">{notesCount}</span>
                              )}
                            </div>
                            <RegisterOrUpdateContactModal contactId={c.ed_contact_id} update={true}
                              toggle_btn={<button className="btn-icon" type="button"><i className="bi bi-pencil-square" /></button>}
                            />
                          </div>
                        </div>
                        {isNotesOpen && (
                          <div style={{ marginTop: 8, borderTop: "1px solid var(--border)", paddingTop: 0 }}>
                            <InlineNotesPanel
                              contact={c}
                              me={me}
                              onClose={() => setOpenInlineNotesId(null)}
                              onNotesLoaded={handleNotesLoaded}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {paginated.length === 0 && (<span className="small-muted">Nenhum contacto encontrado.</span>)}
                </div>
              </div>

              {/* PAGINAÇÃO */}
              <div className="contacts-footer mt-2">
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>«</button>
                    </li>
                    {renderPageNumbers()}
                    <li className={`page-item ${currentPage === pageCount ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}>»</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </Tab>

            <Tab eventKey="pipeline" title="🗂️ Pipeline">
              <PipelineKanban
                contacts={filtered}
                comerciais={comerciais}
                handleStatusChange={handleStatusChange}
                imageUrls={imageUrls}
                me={me}
                onOpenDetails={openDetails}
              />
            </Tab>

            <Tab eventKey="analise" title="Análise de desempenho">
              <div className="contacts-resumo-card">
                <div className="contacts-resumo-title d-flex justify-content-between align-items-center">
                  <span>Visão analítica por comercial</span>
                  <small className="small-muted">Cores = estados</small>
                </div>
                <div ref={chartWrapRef}>
                  {commercialNames.length > 0 ? (
                    <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={340} width="100%" />
                  ) : (
                    <span className="small-muted">Sem dados suficientes para o gráfico.</span>
                  )}
                </div>
                <div className="contacts-resumo-title mt-3">
                  Resumo por Comercial <span className="small-muted">(Qtd / % por estado)</span>
                </div>
                <div className="table-responsive">
                  <table className="contacts-resumo-table">
                    <thead>
                      <tr>
                        <th>Comercial</th><th>Total</th>
                        {(uniqueStatuses.length ? uniqueStatuses : statusOptions.map((s) => s.value)).map((status) => (
                          <th key={status}>{statusLabel(status)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {commercialNames.map((name) => {
                        const total = Object.values(commercialStatusMap[name] || {}).reduce((a, b) => a + b, 0);
                        const statusesToShow = uniqueStatuses.length ? uniqueStatuses : statusOptions.map((s) => s.value);
                        return (
                          <tr key={name}>
                            <td style={{ fontWeight: 700 }}>{name}</td>
                            <td>{total}</td>
                            {statusesToShow.map((status) => {
                              const count = commercialStatusMap[name]?.[status] || 0;
                              const percent = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                              return <td key={status}>{count} / {percent}%</td>;
                            })}
                          </tr>
                        );
                      })}
                      {commercialNames.length === 0 && (
                        <tr><td colSpan={2 + (uniqueStatuses.length ? uniqueStatuses.length : statusOptions.length)}>
                          <span className="small-muted">Nenhum dado para apresentar.</span>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* MODAL DETALHES */}
        <Modal className="pretty-modal" show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-eye me-2" style={{ color: "var(--primary2)" }} />
              {detailsContact ? safeValue(detailsContact.ed_contact_name, "Detalhes do contacto") : "Detalhes"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!detailsContact ? <span className="small-muted">Sem dados.</span> : (
              <>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {getTypeBadge(detailsContact.ed_contact_type)}
                  <span className="pill"><i className="bi bi-person-badge-fill" />{safeValue(detailsContact.ed_contact_comercial, "Sem comercial")}</span>
                  <span className="pill"><i className="bi bi-geo-alt-fill" />{safeValue(detailsContact.ed_contact_province || "Luanda")}</span>
                  <span className={`status-badge ${getStatusClass(detailsContact.ed_contact_status)}`}>
                    <span className="status-dot" />
                    {safeValue(statusLabel(detailsContact.ed_contact_status), "Sem estado")}
                  </span>
                </div>
                <div className="detail-grid">
                  <div className="detail-item"><div className="detail-label">Instituição</div><div className="detail-value">{safeValue(detailsContact.ed_contact_name, "—")}</div></div>
                  <div className="detail-item"><div className="detail-label">Tipo</div><div className="detail-value">{detailsContact.ed_contact_type === 1 ? "Universidade" : "Colégio"}</div></div>
                  <div className="detail-item"><div className="detail-label">Telefone</div><div className="detail-value">{safeValue(detailsContact.ed_contact_phone, "Sem telefone")}</div></div>
                  <div className="detail-item"><div className="detail-label">Email</div>
                    <div className="detail-value">
                      {detailsContact.ed_contact_email ? <a href={`mailto:${detailsContact.ed_contact_email}`} style={{ textDecoration: "none" }}>{detailsContact.ed_contact_email}</a> : "Sem email"}
                    </div>
                  </div>
                  <div className="detail-item" style={{ gridColumn: "1 / -1" }}><div className="detail-label">Endereço</div><div className="detail-value">{safeValue(detailsContact.ed_contact_address, "—")}</div></div>
                  <div className="detail-item"><div className="detail-label">Comercial</div><div className="detail-value">{safeValue(detailsContact.ed_contact_comercial, "—")}</div></div>
                  <div className="detail-item"><div className="detail-label">Província</div><div className="detail-value">{safeValue(detailsContact.ed_contact_province || "Luanda")}</div></div>
                </div>
                <div className="mt-3 d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => { setShowDetailsModal(false); setTimeout(() => toggleInlineNotes(detailsContact.ed_contact_id), 100); }}>
                    <LuStickyNote style={{ marginRight: 6 }} />Ver notas na listagem
                  </Button>
                  <RegisterOrUpdateContactModal contactId={detailsContact.ed_contact_id} update={true}
                    toggle_btn={<Button variant="primary" size="sm"><i className="bi bi-pencil-square me-1" />Editar contacto</Button>}
                  />
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowDetailsModal(false)}>Fechar</Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL EXPORT */}
        <ExportReportModal
          show={showExportModal} onHide={() => setShowExportModal(false)}
          exportKind={exportKind} selectedCommercial={selectedCommercial}
          contact={exportKind === "contact" ? detailsContact : null}
          filteredContacts={filtered} comercialObj={selectedCommercialObj}
          chartImageDataUrl={exportKind === "global" ? chartImageDataUrl : ""}
          statusCounts={statusCounts} commercialStatusMap={commercialStatusMap}
          uniqueStatuses={uniqueStatuses} commercialNames={commercialNames}
          me={me}
        />

        {/* MODAL TRABALHO DIÁRIO FULLSCREEN */}
        <DailyWorkModal
          show={showDailyModal} onHide={() => setShowDailyModal(false)}
          dayKey={dayKey} contactsById={contactsById}
          dailyIds={dailyIds} setDailyIds={setDailyIds}
          handleStatusChange={handleStatusChange}
          reloadContacts={loadContacts}
          imageUrls={imageUrls} setImageUrls={setImageUrls}
          me={me}
        />
      </div>
    </>
  );
};

export default ContactsListTable;
/*


quero criar neste meu code do conatctos usma dividção em tabs no primeiro tab steremso tudo que temos atualmente no segundo tab teremso um pipiline 
para. so emsu conatctos cada  coluna kanbam do meu pipline represneta um estado ex: parados, em andmaento ,etc. 
podemos movem um detereiando conatco para outro kanbma de etsaod e ataulizar automatciamnte o estado conforme fazemso no dropwdn de estados na tabela. o kanabma de colunas o nsoso pilien tera  o degn da iamgem ema nexo
se escola tem uma imagem podmeo no card. do kanbam ter opção de ter essa imagem no vcarad abaixo como titutlo o nome da escola e abaixo conatcos e um button para abir ver oss detalhes e as notas ou sas anotaçeos ja temo um mdoal paar isoso podes reutttilizara. 
podmeo mover um caad ou cionatco para outra coluna de estado e naturalmnte semso capse de ataualiza automatimnte. o estado.fazer isoso e devolver fullcode do meu file ContactsListTable.jsx


*/